import React from "react";
// Consumer Location Modal

// Default
import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";

// Mui
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import Stack from "@mui/material/Stack";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Tab from "@mui/material/Tab";
import TabContext from "@mui/lab/TabContext";
import TabList from "@mui/lab/TabList";
import TabPanel from "@mui/lab/TabPanel";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Grid from "@mui/material/Grid2";
import Divider from "@mui/material/Divider";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import Stepper from "@mui/material/Stepper";
import Step from "@mui/material/Step";
import StepLabel from "@mui/material/StepLabel";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Button from "@mui/material/Button";
import CardHeader from "@mui/material/CardHeader";

// Custom
import CustomInput from "../../forms/input/Input";
import CustomOutlinedSelect from "../../forms/select/OutlinedCustomSelect";
import ContainedButton from "../../forms/button/ContainedButton";
import CustomSearch from "../search/Search";
import DatePickerInput from "../../forms/input/DatePickerInput";
import CustomSwitch from "../switch/Switch";

// React icons
import { FaRegTrashAlt } from "react-icons/fa";

// Utils
import { fetchGETRequest, fetchPOSTRequest } from "../../utils/services";
import { useCustomFields } from "../../context/CustomFieldsProvider";
import moment from "moment";
import { formatDate } from "../../utils/helper";
import {
  DEFAULT_METER_FIELDS,
  getTypeSpecificFields,
  METER_TYPE_IDS,
} from "../../utils/meterFieldsConfig";
import { getTariffsV2 } from "../../utils/tariffServiceV2";
import Grid2 from "@mui/material/Grid2";
import { useAppSelector } from "../../redux/hooks";
import { CreateMeterRequestData, TariffResponseT } from "../../types";
import { UnifiedIcon } from "../Icon/UnifiedIcon";
import { getTypeSpecificConsumerFields } from "../../utils/consumerFieldsConfig";
import { renderTypeSpecificField } from "../../utils/formFieldRenderer";
import StatusModal from "./StatusModal";
import ResumeSessionModal from "./ResumeSessionModal";

const steps = [
  "Create Consumer",
  "Create Site",
  "Create Contract",
  "Create Meter",
];

interface OptionType {
  value: string;
  label: string;
}

interface FormField {
  name: string;
  label: string;
  type: string;
  placeholder?: string;
  options?: OptionType[];
  required: boolean;
}

// Custom hook for data persistence
const useStepDataPersistence = () => {
  const STORAGE_KEY = "createConsumerModelData";

  const saveStepData = (stepIndex: number, data: any) => {
    try {
      const existingData = JSON.parse(
        localStorage.getItem(STORAGE_KEY) || "{}",
      );
      existingData[`step_${stepIndex}`] = data;
      localStorage.setItem(STORAGE_KEY, JSON.stringify(existingData));
    } catch (error) {
      console.error("Error saving step data:", error);
    }
  };

  const loadStepData = (stepIndex: number) => {
    try {
      const existingData = JSON.parse(
        localStorage.getItem(STORAGE_KEY) || "{}",
      );
      return existingData[`step_${stepIndex}`] || null;
    } catch (error) {
      console.error("Error loading step data:", error);
      return null;
    }
  };

  const clearAllData = () => {
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch (error) {
      console.error("Error clearing step data:", error);
    }
  };

  const getAllData = () => {
    try {
      return JSON.parse(localStorage.getItem(STORAGE_KEY) || "{}");
    } catch (error) {
      console.error("Error getting all step data:", error);
      return {};
    }
  };

  return {
    saveStepData,
    loadStepData,
    clearAllData,
    getAllData,
  };
};

interface CreateConsumerModelProps {
  openModal: { type: string; id: string; onlyMeter: boolean };
  setOpenModal: (open: {
    id: string;
    type: string;
    onlyMeter: boolean;
  }) => void;
  setToast: (toast: any) => void;
  addOrEdit: "add" | "edit" | null;
  handleCustomerAndAssetSearch: () => void;
}

// Helper function to extract error message from API response
const extractErrorMessage = (error: any): string => {
  // Check if error has the expected API response format: {status: 400, message: "...", errorCode: 31}
  // This handles axios error responses (when axios throws)
  if (error?.response?.data?.message) {
    return error.response.data.message;
  }

  // Check if error.data has the message (some APIs return data directly)
  if (error?.data?.message) {
    return error.data.message;
  }

  // Check if it's a response object (not an error) with error data
  // This handles cases where fetchPOSTRequest returns a response with error status
  // The API returns: {status: 400, message: "...", errorCode: 31, timestamp: ...}
  if (error?.data && typeof error.data === "object") {
    if (error.data.message) return error.data.message;
    if (error.data.error) return error.data.error;
  }

  // Check if error itself is the API error object with message at root
  // Handles: {status: 400, message: "Customer with such title already exists!", errorCode: 31}
  if (error?.message && typeof error.message === "string" && error.status) {
    return error.message;
  }

  // Check if error itself is a response object with message at root (no status property)
  if (error?.message && typeof error.message === "string") {
    return error.message;
  }

  // Check for axios error format
  if (error?.response?.data) {
    // Sometimes the error message is nested differently
    const data = error.response.data;
    if (data.message) return data.message;
    if (data.error) return data.error;
    if (typeof data === "string") return data;
  }

  // Check for network errors
  if (error?.message && error.message.includes("Network Error")) {
    return "Network error. Please check your connection and try again.";
  }

  // Default error message
  return "An error occurred. Please try again.";
};

// Extend Window interface to include global functions
declare global {
  interface Window {
    handleUpdateConsumer?: (skipNext?: boolean) => void;
    handleSubmitContract?: (skipNext?: boolean) => void;
    handleSubmitSite?: (skipNext?: boolean) => void;
    handleSaveMeter?: (skipNext?: boolean) => void;
    setIsLoading?: (loading: boolean) => void;
  }
}

const CreateConsumerModel = ({
  openModal,
  setOpenModal,
  setToast,
  addOrEdit,
}: CreateConsumerModelProps) => {
  const createOnlyMeter = openModal.onlyMeter;
  const [activeStep, setActiveStep] = useState(0);
  const [stepUUIDs, setStepUUIDs] = useState({
    consumer: null,
    site: null,
    contract: null,
    meter: null,
  });
  const [isLoading, setIsLoading] = useState(false);

  const { saveStepData, loadStepData, clearAllData, getAllData } =
    useStepDataPersistence();

  // Check for existing data when modal opens for the first time
  const [hasShownResumeDialog, setHasShownResumeDialog] = useState(false);
  const [shouldStartFresh, setShouldStartFresh] = useState(false);

  // Status modal state
  const [statusModal, setStatusModal] = useState<{
    open: boolean;
    type: "success" | "error" | "warning";
    message: string;
    title?: string;
    actions?: {
      primary?: { label: string; onClick: () => void };
      secondary?: { label: string; onClick: () => void };
      tertiary?: { label: string; onClick: () => void };
    };
  }>({ open: false, type: "success", message: "" });

  const [resumeModalOpen, setResumeModalOpen] = useState(false);

  const showStatus = (
    type: "success" | "error" | "warning",
    message: string,
    title?: string,
    actions?: any,
  ) => {
    setStatusModal({ open: true, type, message, title, actions });
  };

  const handleStatusModalClose = () => {
    setStatusModal((prev) => ({ ...prev, open: false }));
  };

  // Reset dialog state when modal closes
  useEffect(() => {
    if (typeof openModal === "object" && openModal?.type !== "CREATENEW") {
      setHasShownResumeDialog(false);
      setShouldStartFresh(false);
    }
  }, [openModal]);

  // Check for existing data when modal opens
  useEffect(() => {
    if (
      typeof openModal === "object" &&
      openModal?.type === "CREATENEW" &&
      !hasShownResumeDialog
    ) {
      const existingData = getAllData();
      if (Object.keys(existingData).length > 0) {
        console.log(`🔄 existingData:`, existingData);
        // If there's existing data, show the resume modal
        setResumeModalOpen(true);
      } else {
        setHasShownResumeDialog(true);
      }
    }
  }, [openModal, hasShownResumeDialog]); // Removed hasShownResumeDialog as it's handled by modal state

  const handleResumeSession = () => {
    setResumeModalOpen(false);
    setHasShownResumeDialog(true);
  };

  const handleStartFresh = () => {
    // User chose to start fresh, clear all data immediately
    clearAllData();
    setStepUUIDs({
      consumer: null,
      site: null,
      contract: null,
      meter: null,
    });
    setShouldStartFresh(true);

    if (createOnlyMeter) {
      setActiveStep(3); // Jump to last step
    } else {
      setActiveStep(0);
    }
    setResumeModalOpen(false);
    setHasShownResumeDialog(true);
  };

  useEffect(() => {
    if (createOnlyMeter) {
      setActiveStep(3); // Jump to last step
    } else {
      setActiveStep(0);
    }
  }, [createOnlyMeter]);

  const handleNext = () => {
    if (createOnlyMeter) return; // prevent moving forward
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    if (createOnlyMeter) return; // prevent going backward
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleStepClick = (index: number) => {
    if (createOnlyMeter && index !== 3) return; // prevent clicking non-meter steps
    setActiveStep(index);
  };

  // Expose setIsLoading to window for child components
  useEffect(() => {
    window.setIsLoading = setIsLoading;
    return () => {
      delete window.setIsLoading;
    };
  }, []);

  const handleSave = (skipNext?: boolean) => {
    // Call the appropriate save function based on the current active step
    switch (activeStep) {
      case 0:
        // Save Consumer
        if (typeof window.handleUpdateConsumer === "function") {
          window.handleUpdateConsumer(skipNext);
        }
        break;
      case 1:
        // Save Site
        if (typeof window.handleSubmitSite === "function") {
          window.handleSubmitSite(skipNext);
        }
        break;
      case 2:
        // Save Contract
        if (typeof window.handleSubmitContract === "function") {
          window.handleSubmitContract(skipNext);
        }
        break;
      case 3:
        // Save Meter
        if (typeof window.handleSaveMeter === "function") {
          window.handleSaveMeter(skipNext);
        }
        break;
      default:
        console.log("Save clicked for step:", activeStep, steps[activeStep]);
        break;
    }
  };

  const updateStepUUID = (stepKey: string, uuid: string) => {
    setStepUUIDs((prev) => ({
      ...prev,
      [stepKey]: uuid,
    }));
  };

  // Helper function to update consumer with asset IDs
  const updateConsumerWithAssetId = async (
    consumerId: string,
    assetType: string,
    assetId: string,
  ) => {
    // console.log(`🔄 updateConsumerWithAssetId called:`);
    // console.log(`- consumerId: ${consumerId}`);
    // console.log(`- assetType: ${assetType}`);
    // console.log(`- assetId: ${assetId}`);

    // Input validation
    if (!consumerId || consumerId === "undefined") {
      const error = new Error("Consumer ID is undefined or invalid");
      // console.error("❌ Validation failed:", error.message);
      throw error;
    }

    if (!assetType || assetType === "undefined") {
      const error = new Error("Asset type is undefined or invalid");
      // console.error("❌ Validation failed:", error.message);
      throw error;
    }

    if (!assetId || assetId === "undefined") {
      const error = new Error("Asset ID is undefined or invalid");
      // console.error("❌ Validation failed:", error.message);
      throw error;
    }

    try {
      // First, get the current consumer data
      // console.log(`📞 Fetching current consumer data for ID: ${consumerId}`);
      const consumerResponse = await fetchGETRequest(
        `customer/${consumerId}`,
        {},
        true,
      );

      // console.log(`📥 Consumer fetch response:`, consumerResponse);

      if (consumerResponse?.data) {
        // console.log(`✅ Consumer data retrieved successfully`);
        // console.log(
        //   `📊 Current consumer data:`,
        //   JSON.stringify(consumerResponse.data, null, 2)
        // );

        const currentAdditionalInfo =
          consumerResponse.data.additionalInfo || {};
        const assetKey = `${assetType}Ids`; // siteIds, contractIds, meterIds
        const currentAssetIds = currentAdditionalInfo[assetKey] || [];

        // console.log(`📋 Current ${assetKey}:`, currentAssetIds);

        // Add the new asset ID if it's not already present
        if (!currentAssetIds.includes(assetId)) {
          currentAssetIds.push(assetId);
          // console.log(`➕ Added new ${assetType} ID: ${assetId}`);
        } else {
          // console.log(`⚠️ Asset ID ${assetId} already exists in ${assetKey}`);
        }

        // console.log(`📋 Updated ${assetKey}:`, currentAssetIds);

        // Validate consumer data structure before building update payload
        if (!consumerResponse.data.id || !consumerResponse.data.id.id) {
          throw new Error("Consumer ID is missing in response data");
        }

        if (
          !consumerResponse.data.tenantId ||
          !consumerResponse.data.tenantId.id
        ) {
          throw new Error("Tenant ID is missing in response data");
        }

        // Construct clean payload (similar to working EditConsumer component)
        const updateData = {
          id: consumerResponse.data.id,
          tenantId: consumerResponse.data.tenantId,
          title: consumerResponse.data.title || "",
          additionalInfo: {
            ...currentAdditionalInfo,
            [assetKey]: currentAssetIds,
          },
          name: consumerResponse.data.name || "",
          email: consumerResponse.data.email || "",
          phone: consumerResponse.data.phone || "",
          // Only include non-null fields
          ...(consumerResponse.data.country && {
            country: consumerResponse.data.country,
          }),
          ...(consumerResponse.data.state && {
            state: consumerResponse.data.state,
          }),
          ...(consumerResponse.data.city && {
            city: consumerResponse.data.city,
          }),
          ...(consumerResponse.data.address && {
            address: consumerResponse.data.address,
          }),
          ...(consumerResponse.data.address2 && {
            address2: consumerResponse.data.address2,
          }),
          ...(consumerResponse.data.zip && { zip: consumerResponse.data.zip }),
          ...(consumerResponse.data.externalId && {
            externalId: consumerResponse.data.externalId,
          }),
        };

        // console.log(
        //   `📤 Sending consumer update request:`,
        //   JSON.stringify(updateData, null, 2)
        // );

        const updateResponse = await fetchPOSTRequest(
          `customer`,
          updateData,
          true,
        );
        // console.log(`📥 Consumer update response:`, updateResponse);

        if (updateResponse?.status === 200) {
          // console.log(
          //   `✅ Consumer updated successfully with ${assetType} ID: ${assetId}`
          // );
        } else {
          // console.error(`❌ Consumer update failed:`, updateResponse);
          throw new Error(
            `Consumer update failed with status: ${updateResponse?.status}`,
          );
        }
      } else {
        // console.error(`❌ No consumer data received for ID: ${consumerId}`);
        throw new Error(`No consumer data received for ID: ${consumerId}`);
      }
    } catch (error) {
      // console.error(`❌ Error updating consumer with ${assetType} ID:`, error);
      // console.error("Error details:", {
      //   message: error.message,
      //   response: error.response?.data,
      //   status: error.response?.status,
      //   consumerId,
      //   assetType,
      //   assetId,
      // });
      throw error; // Re-throw to handle in calling function
    }
  };

  // Handle final completion - clear all persisted data
  const handleFinalCompletion = (closeModal = true) => {
    clearAllData();
    setStepUUIDs({
      consumer: null,
      site: null,
      contract: null,
      meter: null,
    });
    setActiveStep(0);
    setHasShownResumeDialog(true);
    setShouldStartFresh(true);
    if (closeModal) {
      setOpenModal({ type: "", id: "", onlyMeter: false });
    }
  };

  const RenderStepperView = useMemo(() => {
    switch (activeStep) {
      case 0:
        return (
          <AddConsumer
            id={typeof openModal === "object" ? openModal?.id : undefined}
            setToast={setToast}
            showStatus={showStatus}
            activeStep={activeStep}
            next={handleNext}
            back={handleBack}
            updateStepUUID={updateStepUUID}
            stepUUIDs={stepUUIDs}
            saveStepData={saveStepData}
            loadStepData={loadStepData}
            shouldStartFresh={shouldStartFresh}
            setShouldStartFresh={setShouldStartFresh}
          />
        );
      case 1:
        return (
          <AddSite
            id={typeof openModal === "object" ? openModal?.id : undefined}
            setToast={setToast}
            showStatus={showStatus}
            activeStep={activeStep}
            next={handleNext}
            back={handleBack}
            updateStepUUID={updateStepUUID}
            stepUUIDs={stepUUIDs}
            saveStepData={saveStepData}
            loadStepData={loadStepData}
            shouldStartFresh={shouldStartFresh}
            setShouldStartFresh={setShouldStartFresh}
            updateConsumerWithAssetId={updateConsumerWithAssetId}
          />
        );
      case 2:
        return (
          <AddContract
            id={typeof openModal === "object" ? openModal?.id : undefined}
            setToast={setToast}
            showStatus={showStatus}
            activeStep={activeStep}
            next={handleNext}
            back={handleBack}
            updateStepUUID={updateStepUUID}
            stepUUIDs={stepUUIDs}
            saveStepData={saveStepData}
            loadStepData={loadStepData}
            shouldStartFresh={shouldStartFresh}
            setShouldStartFresh={setShouldStartFresh}
            updateConsumerWithAssetId={updateConsumerWithAssetId}
          />
        );
      case 3:
        return (
          <AddMeter
            id={typeof openModal === "object" ? openModal?.id : undefined}
            setToast={setToast}
            showStatus={showStatus}
            handleStatusModalClose={handleStatusModalClose}
            setOpenModal={setOpenModal}
            activeStep={activeStep}
            next={handleNext}
            back={handleBack}
            updateStepUUID={updateStepUUID}
            stepUUIDs={stepUUIDs}
            saveStepData={saveStepData}
            loadStepData={loadStepData}
            onFinalCompletion={handleFinalCompletion}
            shouldStartFresh={shouldStartFresh}
            setShouldStartFresh={setShouldStartFresh}
            updateConsumerWithAssetId={updateConsumerWithAssetId}
          />
        );
      default:
        return null;
    }
  }, [activeStep, openModal, setToast, stepUUIDs, shouldStartFresh]);

  return (
    <Dialog
      onClose={() => {
        // Don't clear data when modal is closed - let user resume next time
        setOpenModal({ type: "", id: "", onlyMeter: true });
      }}
      open={openModal?.type === "CREATENEW"}
      fullWidth
      maxWidth={"md"}
      disableEscapeKeyDown={false}
      onBackdropClick={(event) => {
        // Prevent closing when clicking on date picker popper or MUI menus
        const target = event.target as HTMLElement;
        if (
          target.closest(".react-datepicker-popper") ||
          target.closest(".react-datepicker") ||
          target.closest(".MuiPopover-root") ||
          target.closest(".MuiMenu-root") ||
          target.closest(".MuiPaper-root") ||
          target.closest(".MuiList-root") ||
          target.closest(".MuiMenuItem-root") ||
          target.closest("[role='listbox']") ||
          target.closest("[role='option']")
        ) {
          event.stopPropagation();
          event.preventDefault();
          return false;
        }
        setOpenModal({ type: "", id: "", onlyMeter: true });
      }}
      sx={{
        zIndex: 999,
        "& .MuiDialog-paper": {
          height: "90vh",
          display: "flex",
          flexDirection: "column",
          position: "relative",
        },
        "& .react-datepicker-popper": {
          zIndex: 10000,
        },
      }}
    >
      <Stack
        direction={"row"}
        justifyContent={"space-between"}
        alignItems={"center"}
      >
        <DialogTitle sx={{ m: 0, p: 2 }}>{steps[activeStep]}</DialogTitle>
        <IconButton
          onClick={() => {
            // Don't clear data when modal is closed - let user resume next time
            setOpenModal({ type: "", id: "", onlyMeter: true });
          }}
          sx={{ marginRight: "10px" }}
        >
          <CloseIcon />
        </IconButton>
      </Stack>

      <DialogContent
        dividers
        sx={{
          flex: 1,
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* Stepper */}
        {/* <Stepper activeStep={activeStep} alternativeLabel>
          {steps.map((label, index) => (
            <Step key={label}>
              <StepLabel
                sx={{
                  "& .MuiStepIcon-root.Mui-active": { color: "#1976d2" },
                  "& .MuiStepIcon-root.Mui-completed": { color: "#0396FF" },
                  "& .MuiStepLabel-root": {
                    cursor: "pointer",
                  },
                  "& .MuiStepLabel-label": {
                    cursor: "pointer",
                    textDecoration: "none",
                    color: activeStep === index ? "#1976d2" : "#666",
                    fontWeight: activeStep === index ? 600 : 400,
                    "&:hover": {
                      textDecoration: "underline",
                      color: "#1976d2",
                    },
                  },
                }}
                onClick={() => handleStepClick(index)}>
                {label}
              </StepLabel>
            </Step>
          ))}
        </Stepper> */}

        <Stepper activeStep={activeStep} alternativeLabel>
          {steps.map((label, index) => (
            <Step key={label}>
              <StepLabel
                sx={{
                  "& .MuiStepIcon-root.Mui-active": { color: "#1064ED" },
                  "& .MuiStepIcon-root.Mui-completed": { color: "#1064ED" },
                  "& .MuiStepLabel-root": {
                    cursor:
                      createOnlyMeter && index !== 3
                        ? "not-allowed"
                        : "pointer",
                  },
                  "& .MuiStepLabel-label": {
                    cursor:
                      createOnlyMeter && index !== 3
                        ? "not-allowed"
                        : "pointer",
                    textDecoration: "none",
                    color:
                      activeStep === index
                        ? "#1064ED"
                        : createOnlyMeter && index !== 3
                          ? "#999"
                          : "#666",
                    fontWeight: activeStep === index ? 600 : 400,
                    fontSize: 15,
                    "&:hover": {
                      textDecoration:
                        createOnlyMeter && index !== 3 ? "none" : "underline",
                      color:
                        createOnlyMeter && index !== 3 ? "#999" : "#1064ED",
                    },
                  },
                }}
                // onClick={() => handleStepClick(index)}
              >
                {label}
              </StepLabel>
            </Step>
          ))}
        </Stepper>

        {/* Single UUID Display Box */}
        <Box>
          <Stack
            sx={{
              p: 1,
              my: 1,
              maxWidth: "85%",
              mx: "auto",
              backgroundColor: "#f8f9fa",
              border: "1px solid #e0e0e0",
              borderRadius: 2,
            }}
          >
            <Grid2 container={true} rowSpacing={1} columnSpacing={2}>
              <Grid2 size={{ xs: 12, md: 6, lg: 6 }}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Typography
                    variant="body1"
                    sx={{ fontWeight: 500, color: "#666" }}
                  >
                    Consumer ID:
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      fontFamily: "monospace",
                      backgroundColor: stepUUIDs.consumer
                        ? "#e8f5e8"
                        : "#f0f0f0",
                      color: stepUUIDs.consumer ? "#2e7d32" : "#999",
                      px: 1,
                      py: 0.5,
                      borderRadius: 1,
                      fontSize: "0.75rem",
                      border: stepUUIDs.consumer
                        ? "1px solid #4caf50"
                        : "1px solid #ddd",
                    }}
                  >
                    {stepUUIDs.consumer || "Pending"}
                  </Typography>
                </Box>
              </Grid2>
              <Grid2 size={{ xs: 12, md: 6, lg: 6 }}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Typography
                    variant="body1"
                    sx={{ fontWeight: 500, color: "#666" }}
                  >
                    Site ID:
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      fontFamily: "monospace",
                      backgroundColor: stepUUIDs.site ? "#e8f5e8" : "#f0f0f0",
                      color: stepUUIDs.site ? "#2e7d32" : "#999",
                      px: 1,
                      py: 0.5,
                      borderRadius: 1,
                      fontSize: "0.75rem",
                      border: stepUUIDs.site
                        ? "1px solid #4caf50"
                        : "1px solid #ddd",
                    }}
                  >
                    {stepUUIDs.site || "Pending"}
                  </Typography>
                </Box>
              </Grid2>
              <Grid2 size={{ xs: 12, md: 6, lg: 6 }}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Typography
                    variant="body1"
                    sx={{ fontWeight: 500, color: "#666" }}
                  >
                    Contract ID:
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      fontFamily: "monospace",
                      backgroundColor: stepUUIDs.contract
                        ? "#e8f5e8"
                        : "#f0f0f0",
                      color: stepUUIDs.contract ? "#2e7d32" : "#999",
                      px: 1,
                      py: 0.5,
                      borderRadius: 1,
                      fontSize: "0.75rem",
                      border: stepUUIDs.contract
                        ? "1px solid #4caf50"
                        : "1px solid #ddd",
                    }}
                  >
                    {stepUUIDs.contract || "Pending"}
                  </Typography>
                </Box>
              </Grid2>
              <Grid2 size={{ xs: 12, md: 6, lg: 6 }}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Typography
                    variant="body1"
                    sx={{ fontWeight: 500, color: "#666" }}
                  >
                    Meter ID:
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      fontFamily: "monospace",
                      backgroundColor: stepUUIDs.meter ? "#e8f5e8" : "#f0f0f0",
                      color: stepUUIDs.meter ? "#2e7d32" : "#999",
                      px: 1,
                      py: 0.5,
                      borderRadius: 1,
                      fontSize: "0.75rem",
                      border: stepUUIDs.meter
                        ? "1px solid #4caf50"
                        : "1px solid #ddd",
                    }}
                  >
                    {stepUUIDs.meter || "Pending"}
                  </Typography>
                </Box>
              </Grid2>
            </Grid2>
          </Stack>
        </Box>

        <Box sx={{ flex: 1, overflow: "auto", mt: 0 }}>{RenderStepperView}</Box>
      </DialogContent>

      {/* Global Footer for all steps */}
      <Divider />
      <DialogActions
        sx={{
          position: "sticky",
          bottom: 0,
          backgroundColor: "white",
          zIndex: 10,
          borderTop: "1px solid #e0e0e0",
          boxShadow: "0 -2px 10px rgba(0,0,0,0.1)",
          padding: "16px 24px",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            gap: 2,
            width: "100%",
          }}
        >
          <Button
            variant="outlined"
            disabled={activeStep === 0 || createOnlyMeter}
            sx={{ textTransform: "none" }}
            onClick={handleBack}
          >
            Back
          </Button>
          <Box sx={{ flex: "1 1 auto" }} />
          <ContainedButton
            label={
              stepUUIDs[
                Object.keys(stepUUIDs)[activeStep] as keyof typeof stepUUIDs
              ]
                ? "Update"
                : activeStep === steps.length - 1
                  ? "Save"
                  : "Save & Next"
            }
            onClick={() =>
              handleSave(
                !!stepUUIDs[
                  Object.keys(stepUUIDs)[activeStep] as keyof typeof stepUUIDs
                ],
              )
            }
            loading={isLoading}
            disabled={isLoading}
            sx={{
              width: "fit-content",
              fontWeight: 600,
              color: "#ffffff",
            }}
          />
          {stepUUIDs[
            Object.keys(stepUUIDs)[activeStep] as keyof typeof stepUUIDs
          ] &&
            activeStep !== steps.length - 1 && (
              <Button
                variant="outlined"
                sx={{ textTransform: "none" }}
                onClick={handleNext}
              >
                Next
              </Button>
            )}
        </Box>
      </DialogActions>

      {/* Status Modal */}
      <StatusModal
        open={statusModal.open}
        onClose={handleStatusModalClose}
        type={statusModal.type}
        message={statusModal.message}
        title={statusModal.title}
        actions={statusModal.actions}
      />
      <ResumeSessionModal
        open={resumeModalOpen}
        onResume={handleResumeSession}
        onStartFresh={handleStartFresh}
      />
    </Dialog>
  );
};

export default CreateConsumerModel;

// Component interfaces
interface AddConsumerProps {
  id?: string;
  setToast: (toast: any) => void;
  showStatus: (
    type: "success" | "error" | "warning",
    message: string,
    title?: string,
    actions?: any,
  ) => void;
  activeStep: number;
  next: () => void;
  back: () => void;
  updateStepUUID: (stepKey: string, uuid: string) => void;
  stepUUIDs: any;
  saveStepData: (stepIndex: number, data: any) => void;
  loadStepData: (stepIndex: number) => any;
  shouldStartFresh: boolean;
  setShouldStartFresh: (value: boolean) => void;
}

interface AddContractProps {
  id?: string;
  setToast: (toast: any) => void;
  showStatus: (
    type: "success" | "error" | "warning",
    message: string,
    title?: string,
  ) => void;
  activeStep: number;
  next: () => void;
  back: () => void;
  updateStepUUID: (stepKey: string, uuid: string) => void;
  stepUUIDs: any;
  saveStepData: (stepIndex: number, data: any) => void;
  loadStepData: (stepIndex: number) => any;
  shouldStartFresh: boolean;
  setShouldStartFresh: (value: boolean) => void;
  updateConsumerWithAssetId: (
    consumerId: string,
    assetType: string,
    assetId: string,
  ) => Promise<void>;
}

interface AddSiteProps {
  id?: string;
  setToast: (toast: any) => void;
  showStatus: (
    type: "success" | "error" | "warning",
    message: string,
    title?: string,
  ) => void;
  activeStep: number;
  next: () => void;
  back: () => void;
  updateStepUUID: (stepKey: string, uuid: string) => void;
  stepUUIDs: any;
  saveStepData: (stepIndex: number, data: any) => void;
  loadStepData: (stepIndex: number) => any;
  shouldStartFresh: boolean;
  setShouldStartFresh: (value: boolean) => void;
  updateConsumerWithAssetId: (
    consumerId: string,
    assetType: string,
    assetId: string,
  ) => Promise<void>;
}

interface AddMeterProps {
  id?: string;
  setToast: (toast: any) => void;
  showStatus: (
    type: "success" | "error" | "warning",
    message: string,
    title?: string,
    actions?: any,
  ) => void;
  handleStatusModalClose: () => void;
  setOpenModal: (open: {
    id: string;
    type: string;
    onlyMeter: boolean;
  }) => void;
  activeStep: number;
  next: () => void;
  back: () => void;
  updateStepUUID: (stepKey: string, uuid: string) => void;
  stepUUIDs: any;
  saveStepData: (stepIndex: number, data: any) => void;
  loadStepData: (stepIndex: number) => any;
  onFinalCompletion: (closeModal?: boolean) => void;
  shouldStartFresh: boolean;
  setShouldStartFresh: (value: boolean) => void;
  updateConsumerWithAssetId: (
    consumerId: string,
    assetType: string,
    assetId: string,
  ) => Promise<void>;
}

const AddConsumer = ({
  id,
  setToast,
  showStatus,
  activeStep,
  next,
  back,
  updateStepUUID,
  stepUUIDs,
  saveStepData,
  loadStepData,
  shouldStartFresh,
  setShouldStartFresh,
}: AddConsumerProps) => {
  const [searchValue, setSearchValue] = useState({
    type: "id",
    value: "",
  });
  const [activeTab, setActiveTab] = useState("Consumer Details");
  const [contactDetails, setContactDetails] = useState<
    Array<{ type: string; value: string }>
  >([]);
  const prevDataRef = useRef<any>(null);
  const [contactType, setContactType] = useState<any>(null);
  const [contactValue, setContactValue] = useState<string>("");
  const [identityDetails, setIdentityDetails] = useState<
    Array<{ type: string; value: string }>
  >([]);
  const [identityType, setIdentityType] = useState<any>(null);
  const [identityValue, setIdentityValue] = useState<string>("");
  const [names, setNames] = useState<string[]>([]);
  const [createCustomer, setCreateCustomer] = useState<{
    id: { id: string; entityType: string };
    customerType: any;
    customerBillingType: any;
    salutation: any;
    firstName: string;
    lastName: string;
    customerCreationDate: Date;
    customerBirthDate: Date;
    consumerId: string;
    mobileNumber: string;
    emailId: string;
    billingFrequency: any;
    billingLevel: any;
    [key: string]: any;
  }>({
    id: { id: "", entityType: "" },
    customerType: "",
    customerBillingType: "",
    salutation: "",
    firstName: "",
    lastName: "",
    customerCreationDate: new Date(),
    customerBirthDate: new Date(),
    consumerId: "",
    mobileNumber: "",
    emailId: "",
    billingFrequency: "",
    billingLevel: "",
  });

  const { toggleCustomFieldDrawer, createdFields } = useCustomFields();

  const resetFormData = () => {
    setCreateCustomer({
      id: { id: "", entityType: "" },
      customerType: "",
      customerBillingType: "",
      salutation: "",
      firstName: "",
      lastName: "",
      customerCreationDate: new Date(),
      customerBirthDate: new Date(),
      consumerId: "",
      mobileNumber: "",
      emailId: "",
      billingFrequency: "",
      billingLevel: "",
    });
    setContactDetails([]);
    setIdentityDetails([]);

    setNames([]);
    setSearchValue({
      type: "id",
      value: "",
    });
  };

  // Handle start fresh logic
  useEffect(() => {
    if (shouldStartFresh) {
      resetFormData();
      setShouldStartFresh(false);
    }
  }, [shouldStartFresh, setShouldStartFresh]);

  // Load persisted data on component mount
  useEffect(() => {
    if (!shouldStartFresh) {
      const persistedData = loadStepData(0);
      if (persistedData) {
        setCreateCustomer(persistedData.createCustomer || createCustomer);
        setContactDetails(persistedData.contactDetails || []);
        setIdentityDetails(persistedData.identityDetails || []);
        setContactType(persistedData.contactType || null);
        setContactValue(persistedData.contactValue || "");
        setIdentityType(persistedData.identityType || null);
        setIdentityValue(persistedData.identityValue || "");
        setActiveTab(persistedData.activeTab || "Consumer Details");
      }
    }
  }, [shouldStartFresh]);

  // Save data whenever form state changes
  // useEffect(() => {
  //   if (!shouldStartFresh) {
  //     const dataToSave = {
  //       createCustomer,
  //       contactDetails,
  //       identityDetails,
  //       contactType,
  //       contactValue,
  //       identityType,
  //       identityValue,
  //       activeTab,
  //     };
  //     saveStepData(0, dataToSave);
  //   }
  // }, [
  //   createCustomer,
  //   contactDetails,
  //   identityDetails,
  //   contactType,
  //   contactValue,
  //   identityType,
  //   identityValue,
  //   // activeTab,
  //   shouldStartFresh,
  // ]);

  useEffect(() => {
    const dataToSave = {
      createCustomer,
      contactDetails,
      identityDetails,
      contactType,
      contactValue,
      identityType,
      identityValue,
      activeTab,
    };

    const prevData = prevDataRef.current;

    const isDataChanged =
      JSON.stringify(prevData) !== JSON.stringify(dataToSave);

    if (!shouldStartFresh && isDataChanged) {
      console.log(`🔄 dataToSave:`, dataToSave);
      saveStepData(0, dataToSave);
      prevDataRef.current = dataToSave; // update ref only when saving
    }
  }, [
    createCustomer,
    contactDetails,
    identityDetails,
    contactType,
    contactValue,
    identityType,
    identityValue,
    // activeTab,
    shouldStartFresh,
  ]);

  const handleTabChange = (_: React.SyntheticEvent, newValue: string) => {
    setActiveTab(newValue);
  };

  const getCustomFields = (item: any) => {
    switch (item?.type?.label) {
      case "Date":
        return (
          <DatePickerInput
            label={item?.title}
            format={"dd/MM/yyyy"}
            placeholder={"dd/mm/yyyy"}
            value={createCustomer?.[item?.title?.toUpperCase()] || null}
            onChange={(event) => {
              setCreateCustomer({
                ...createCustomer,
                [item?.title?.toUpperCase()]: event,
              });
            }}
          />
        );
      case "Checkbox":
        return (
          <Stack mt={"30px"}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={
                    createCustomer?.[item?.title?.toUpperCase()] || false
                  }
                  onChange={(event) => {
                    setCreateCustomer({
                      ...createCustomer,
                      [item?.title?.toUpperCase()]: event.target.checked,
                    });
                  }}
                  sx={{
                    color: "#1976d2",
                    "&.Mui-checked": {
                      color: "#1976d2",
                    },
                  }}
                />
              }
              label={item?.title}
            />
          </Stack>
        );
      case "Text":
        return (
          <>
            <CustomInput
              label={item?.title}
              placeholder={item?.title}
              type="text"
              value={createCustomer?.[item?.title?.toUpperCase()] || ""}
              onChange={(event) => {
                setCreateCustomer({
                  ...createCustomer,
                  [item?.title?.toUpperCase()]: event.target.value,
                });
              }}
            />
          </>
        );

      case "Number":
        return (
          <>
            <CustomInput
              label={item?.title}
              placeholder={item?.title}
              type="number"
              value={createCustomer?.[item?.title?.toUpperCase()] || ""}
              onChange={(event) => {
                setCreateCustomer({
                  ...createCustomer,
                  [item?.title?.toUpperCase()]: event.target.value,
                });
              }}
            />
          </>
        );

      case "Dropdown":
        return (
          <>
            <CustomOutlinedSelect
              label={item?.title}
              placeholder={item?.title}
              options={item?.options}
              value={createCustomer?.[item?.title?.toUpperCase()] || ""}
              onChange={(event) => {
                setCreateCustomer({
                  ...createCustomer,
                  [item?.title?.toUpperCase()]: event,
                });
              }}
            />
          </>
        );

      default:
        return null;
    }
  };

  const TableContent = (data: { type: string; value: string }[]) => {
    return (
      <TableContainer sx={{ mt: "20px" }}>
        <Table sx={{ border: "1px solid #D7DAE7" }}>
          <TableHead>
            <TableRow>
              <TableCell
                sx={{ paddingLeft: "10px", color: "black !important" }}
                className={"custom_table_thead_cell"}
              >
                Event Type
              </TableCell>

              <TableCell
                sx={{ color: "black !important" }}
                className={"custom_table_thead_cell"}
              >
                Comments
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.map((item) => {
              return (
                <TableRow>
                  <TableCell sx={{ backgroundColor: "#D7DAE7" }}>
                    <Typography className={"custom_table_tbody_cell"}>
                      {item?.type}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography className={"custom_table_tbody_cell"}>
                      {item?.value}
                    </Typography>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  useEffect(() => {
    setSearchValue({ ...searchValue, value: id || "" });
  }, [id]);

  // Expose the save function to the global scope for the footer save button
  useEffect(() => {
    window.handleUpdateConsumer = (skipNext?: boolean) => handleSave(skipNext);
    return () => {
      delete window.handleUpdateConsumer;
    };
  }, [createCustomer, contactDetails, identityDetails]);

  const addContact = () => {
    if (contactType && contactValue) {
      setContactDetails([
        ...contactDetails,
        { type: contactType?.value, value: contactValue },
      ]);
    } else {
      setToast({
        message: "Contact Type and Contact Value are required",
        severity: "error",
      });
    }
  };

  const addIdentity = () => {
    if (identityType && identityValue) {
      setIdentityDetails([
        ...identityDetails,
        { type: identityType?.value, value: identityValue },
      ]);
    } else {
      setToast({
        message: "Identity Type and Identity Value are required",
      });
    }
  };

  const generateUniqueName = () => {
    const randomSuffix = Math.floor(Math.random() * 1000000000);
    const newName = `consumer${randomSuffix}`;
    setNames([...names, newName]);
    return newName;
  };

  const handleSave = async (skipNext?: boolean) => {
    // Set loading state
    if (window.setIsLoading) {
      window.setIsLoading(true);
    }

    try {
      const requestData = {
        tenantId: {
          id: "6e191ce0-a3e2-11ee-9483-cd24c515eaca",
          entityType: "TENANT",
        },
        // title: generateUniqueName(),
        additionalInfo: {
          salutation: createCustomer.salutation?.value || "",
          consumerType: createCustomer.customerType?.value || "",
          consumerBillingType: createCustomer.customerBillingType?.value || "",
          firstName: createCustomer.firstName || "",
          lastName: createCustomer.lastName || "",
          createdOn: createCustomer.customerCreationDate?.toString() || "",
          dob: createCustomer.customerBirthDate?.toString() || "",
          billingFrequency: createCustomer.billingFrequency?.value || "",
          billingLevel: createCustomer.billingLevel?.value || "",
          status: "active",
          contacts: contactDetails || [],
          identity: identityDetails || [],
          consumerId: createCustomer.consumerId || null,
          // Initialize arrays for associated assets
          siteIds: [],
          contractIds: [],
          meterIds: [],
        },
        name:
          (createCustomer.firstName || "") +
          " " +
          (createCustomer.lastName || ""),
        // externalId: createCustomer.consumerId,
        email: createCustomer.emailId,
        phone: createCustomer.mobileNumber,
        address: "",
        address2: "",
        city: "",
        country: "",
        state: "",
        title:
          (createCustomer.firstName || "") +
          " " +
          (createCustomer.lastName || ""),
        zip: "",
        external_id: "",
        version: 0,

        ...(createCustomer?.id &&
          createCustomer?.id?.id && {
            id: { id: createCustomer?.id?.id, entityType: "ASSET" },
          }),
      };

      // if (createCustomer?.id && addOrEdit === "edit") {
      //   requestData.id = {
      //     id: createCustomer?.id?.id,
      //     entityType: "CUSTOMER",
      //   };
      // }

      console.log("requestData create Consumer", requestData);

      const response = await fetchPOSTRequest(`customer`, requestData, true);

      // Check if response has error status (400, 500, etc.)
      if (response?.status && response.status >= 400) {
        // Handle error response - API returned error status
        const errorData = response?.data || response;
        const errorMessage = extractErrorMessage(errorData);
        setToast({
          message: errorMessage || "Failed to create consumer",
          severity: "error",
        });
        return;
      }

      // Check for successful response
      if (response?.status === 200 && response?.data?.id?.id) {
        // Update the step UUID when consumer is successfully created
        updateStepUUID("consumer", response.data.id.id);

        setNames([]);
        setIdentityDetails([]);
        setSearchValue({
          ...searchValue,
          value: response.data.id.id,
        });
        setContactDetails([]);
        setContactType(null);
        setContactValue("");
        setCreateCustomer({
          id: { id: "", entityType: "" },
          customerType: "",
          customerBillingType: "",
          salutation: "",
          firstName: "",
          lastName: "",
          customerCreationDate: new Date(),
          customerBirthDate: new Date(),
          consumerId: "",
          mobileNumber: "",
          emailId: "",
          billingFrequency: "",
          billingLevel: "",
        });

        showStatus("success", response.data.id.id + " Save successful!");
        if (!skipNext) {
          next();
        }
      } else {
        // Handle unexpected response format
        const errorMessage = extractErrorMessage(response);
        setToast({
          message:
            errorMessage ||
            "Failed to create consumer. Unexpected response format.",
          severity: "error",
        });
      }
    } catch (error: any) {
      console.error("❌ Error during consumer creation:", error);
      console.error("Error structure:", {
        hasResponse: !!error?.response,
        responseStatus: error?.response?.status,
        responseData: error?.response?.data,
        errorMessage: error?.message,
      });

      const errorMessage = extractErrorMessage(error);

      setToast({
        message: errorMessage,
        severity: "error",
      });

      showStatus("error", errorMessage);
    } finally {
      // Reset loading state
      if (window.setIsLoading) {
        window.setIsLoading(false);
      }
    }
  };

  const validateEmail = (email: string) => {
    const regex = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;
    if (!regex.test(email)) {
      setToast({ message: "Invalid email address", severity: "error" });
    }
  };

  const contactTypeOptions = [
    { value: "Phone", label: "Phone" },
    { value: "Email", label: "Email" },
    { value: "Address", label: "Address" },
  ];

  const identificationTypeOptions = [
    { value: "Aadhar", label: "Aadhar" },
    { value: "DL", label: "DL" },
    { value: "SSN", label: "SSN" },
  ];
  const TABS = [
    "Consumer Details",
    "Contact Details",
    "Identification Details",
  ];

  const dynamicTypeSpecificFields = useMemo(() => {
    return getTypeSpecificConsumerFields("consumer");
  }, []);

  return (
    <Box>
      <Card sx={{ mt: "20px" }}>
        <TabContext value={activeTab}>
          <Box
            sx={{
              borderBottom: 1,
              borderColor: "#CACDD7",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <TabList
              onChange={handleTabChange}
              scrollButtons={false}
              TabIndicatorProps={{
                style: {
                  backgroundColor: "##1280E7",
                  height: "3px",
                },
              }}
            >
              {TABS.map((item, ind) => {
                return (
                  <Tab
                    key={ind}
                    label={
                      <Box
                        display="flex"
                        alignItems="center"
                        gap={"7px"}
                        fontWeight={500}
                      >
                        {item}
                      </Box>
                    }
                    value={item}
                    className="consumer_right_card_list_tab"
                  />
                );
              })}
            </TabList>

            <ContainedButton
              label={"Create custom fields"}
              sx={{
                width: "fit-content",
                fontWeight: 600,
                color: "#ffffff",
              }}
              onClick={() => toggleCustomFieldDrawer("Consumer", true)}
            />
          </Box>

          <CardContent>
            <TabPanel value={TABS[0]} sx={{ padding: 0 }}>
              <Grid container spacing={2}>
                {dynamicTypeSpecificFields.map((field) => (
                  <Grid
                    key={field.name}
                    size={
                      field.fullWidth ? { xs: 12 } : { xs: 12, md: 4, lg: 4 }
                    }
                    mt={"10px"}
                  >
                    {renderTypeSpecificField(
                      field,
                      createCustomer,
                      setCreateCustomer,
                    )}
                  </Grid>
                ))}
              </Grid>

              {createdFields?.["Consumer"] && (
                <>
                  <Stack sx={{ paddingTop: "30px", paddingBottom: "20px" }}>
                    <Divider sx={{ borderWidth: "1.5px" }} />
                  </Stack>

                  <Grid container spacing={2}>
                    {createdFields?.["Consumer"]?.map((item: any) => {
                      return (
                        <Grid size={{ xs: 12, md: 4, lg: 4 }} mt={"10px"}>
                          {getCustomFields(item)}
                        </Grid>
                      );
                    })}
                  </Grid>
                </>
              )}
            </TabPanel>

            <TabPanel value={TABS[1]} sx={{ padding: 0 }}>
              <Stack direction={"row"} justifyContent={"space-between"}>
                <Stack width={"75%"}>
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 12, md: 5, lg: 5 }}>
                      <CustomOutlinedSelect
                        placeholder={"Select"}
                        options={contactTypeOptions}
                        label={"Contact Type"}
                        value={contactType}
                        onChange={(newValue) => {
                          setContactType(newValue);
                        }}
                      />
                    </Grid>

                    <Grid size={{ xs: 12, md: 5, lg: 5 }}>
                      <CustomInput
                        label={"Contact Value"}
                        placeholder={"Contact"}
                        value={contactValue}
                        onChange={(e) => {
                          setContactValue(e.target.value);
                          if (contactType?.value === "Email") {
                            validateEmail(e.target.value);
                          }
                        }}
                        type={
                          contactType?.value === "Email"
                            ? "email"
                            : contactType?.value === "Phone"
                              ? "number"
                              : contactType?.value === "Address"
                                ? "text"
                                : "text"
                        }
                      />
                    </Grid>
                    <Grid size={{ xs: 12, md: 2, lg: 2 }} mt={"30px"}>
                      <ContainedButton
                        label={"Add Contact"}
                        sx={{
                          width: "fit-content",
                          fontWeight: 600,
                          height: "35px",
                          color: "#ffffff",
                        }}
                        onClick={addContact}
                      />
                    </Grid>
                  </Grid>
                </Stack>

                {contactDetails && contactDetails?.length > 0 && (
                  <Stack width={"25%"}>{TableContent(contactDetails)}</Stack>
                )}
              </Stack>
            </TabPanel>

            <TabPanel value={TABS[2]} sx={{ padding: 0 }}>
              <Stack direction={"row"} justifyContent={"space-between"}>
                <Stack width={"75%"}>
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 12, md: 5, lg: 5 }}>
                      <CustomOutlinedSelect
                        placeholder={"Select"}
                        options={identificationTypeOptions}
                        label={"Identification Type"}
                        value={identityType}
                        onChange={(newValue) => {
                          setIdentityType(newValue);
                        }}
                      />
                    </Grid>

                    <Grid size={{ xs: 12, md: 5, lg: 5 }}>
                      <CustomInput
                        label={"Identification Value"}
                        placeholder={"Identification"}
                        value={identityValue}
                        onChange={(e) => {
                          setIdentityValue(e.target.value);
                        }}
                        type={
                          identityType?.value === "Aadhar" ? "number" : "text"
                        }
                      />
                    </Grid>
                    <Grid size={{ xs: 12, md: 2, lg: 2 }} mt={"30px"}>
                      <ContainedButton
                        label={"Add Identity"}
                        sx={{
                          width: "fit-content",
                          fontWeight: 600,
                          height: "35px",
                          color: "#ffffff",
                        }}
                        onClick={addIdentity}
                      />
                    </Grid>
                  </Grid>
                </Stack>

                {identityDetails && identityDetails?.length > 0 && (
                  <Stack width={"25%"}>{TableContent(identityDetails)}</Stack>
                )}
              </Stack>
            </TabPanel>
          </CardContent>
        </TabContext>
      </Card>
    </Box>
  );
};

const AddSite = ({
  id,
  setToast,
  showStatus,
  activeStep,
  next,
  back,
  updateStepUUID,
  stepUUIDs,
  saveStepData,
  loadStepData,
  shouldStartFresh,
  setShouldStartFresh,
  updateConsumerWithAssetId,
}: AddSiteProps) => {
  const [searchValue, setSearchValue] = useState({
    type: "id",
    value: "",
  });
  const [searchOpen, setSearchOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("Details");
  const [siteData, setSiteData] = useState<{
    useAsLoadProfile: boolean;
    status: boolean;
    serviceCommodityType: any;
    serviceType: any;
    operatingHours: any;
    latitude: string;
    longitude: string;
    locationId: string;
    substationName: string;
    zoneNumber: string;
    feederName: string;
    dtrName: string;
    feederPillarNo: string;
    meterLocation: string;
    changeOfMeterLocation: string;
    dateOfConnection: Date;
    consumerType: any;
    houseNo: string;
    streetAddress: string;
    city: string;
    state: string;
    zipCode: string;
    divisionCode: string;
    subDivisionCode: string;
    numberOfMeters: string;
    linkToCustomer: string;
    [key: string]: any;
  }>({
    useAsLoadProfile: false,
    status: true, // true = Active, false = Inactive
    serviceCommodityType: "",
    serviceType: "",
    operatingHours: "",
    latitude: "",
    longitude: "",
    locationId: "",
    substationName: "",
    zoneNumber: "",
    feederName: "",
    dtrName: "",
    feederPillarNo: "",
    meterLocation: "",
    changeOfMeterLocation: "",
    dateOfConnection: new Date(),
    // Location fields
    consumerType: "",
    houseNo: "",
    streetAddress: "",
    city: "",
    state: "",
    zipCode: "",
    divisionCode: "",
    subDivisionCode: "",
    numberOfMeters: "",
    linkToCustomer: "",
  });
  const [activeMeters, setActiveMeters] = useState<string[]>([]);
  const [inactiveMeters, setInactiveMeters] = useState<string[]>([]);
  const [names, setNames] = useState<string[]>([]);
  const [counter, setCounter] = useState(1);
  const [siteIdInput, setSiteIdInput] = useState<string[]>([]);

  const navigate = useNavigate();

  const { toggleCustomFieldDrawer, createdFields } = useCustomFields();

  const location = useLocation();

  const resetFormData = () => {
    setSiteData({
      useAsLoadProfile: false,
      status: true, // true = Active, false = Inactive
      serviceCommodityType: "",
      serviceType: "",
      operatingHours: "",
      latitude: "",
      longitude: "",
      locationId: "",
      substationName: "",
      zoneNumber: "",
      feederName: "",
      dtrName: "",
      feederPillarNo: "",
      meterLocation: "",
      changeOfMeterLocation: "",
      dateOfConnection: new Date(),
      // Location fields
      consumerType: "",
      houseNo: "",
      streetAddress: "",
      city: "",
      state: "",
      zipCode: "",
      divisionCode: "",
      subDivisionCode: "",
      numberOfMeters: "",
      linkToCustomer: "",
    });
    setActiveMeters([]);
    setInactiveMeters([]);
    setSiteIdInput([]);
    setActiveTab("Details");
  };

  // Handle start fresh logic
  useEffect(() => {
    if (shouldStartFresh) {
      resetFormData();
      setShouldStartFresh(false);
    }
  }, [shouldStartFresh, setShouldStartFresh]);

  // Load persisted data on component mount
  useEffect(() => {
    if (!shouldStartFresh) {
      const persistedData = loadStepData(1);
      if (persistedData) {
        setSiteData(persistedData.siteData || siteData);
        setActiveMeters(persistedData.activeMeters || []);
        setInactiveMeters(persistedData.inactiveMeters || []);
        setSiteData((prevState) => ({
          ...prevState,
          linkToCustomer: persistedData.linkToCustomer || "",
        }));
        setSiteIdInput(persistedData.siteIdInput || []);
        setActiveTab(persistedData.activeTab || "Details");
      }
    }
  }, [shouldStartFresh]);

  // Save data whenever form state changes
  useEffect(() => {
    if (!shouldStartFresh) {
      const dataToSave = {
        siteData,
        activeMeters,
        inactiveMeters,
        linkToCustomer: siteData.linkToCustomer,
        siteIdInput,
        activeTab,
      };
      saveStepData(1, dataToSave);
    }
  }, [
    siteData,
    activeMeters,
    inactiveMeters,
    siteIdInput,
    activeTab,
    shouldStartFresh,
  ]);

  const TABS = ["Details", "Coordinates", "Meters"];

  const commodityTypeOptions = [
    { label: "Electricity", value: "Electricity" },
    { label: "Water", value: "Water" },
    { label: "Gas", value: "Gas" },
  ];

  const serviceTypeOptions = [
    { label: "Commercial", value: "Commercial" },
    { label: "Residential", value: "Residential" },
  ];

  const consumerTypeOptions = [
    { label: "Personal", value: "Personal" },
    { label: "Business", value: "Business" },
  ];

  const startDate = moment(siteData?.dateOfConnection);
  const today = moment();
  const isActive = startDate?.isBefore(today);

  const addRow = () => {
    setActiveMeters((prevState) => [...prevState, ""]);
  };

  const removeRow = (index: number) => {
    const updatedMeters = activeMeters.filter((_, idx) => index !== idx);
    setActiveMeters([...updatedMeters]);
  };

  const handleMeterIdChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    index: number,
    isActive: boolean,
  ) => {
    const { value } = e.target;
    if (isActive) {
      const updatedActiveMeters = [...activeMeters];
      updatedActiveMeters[index] = value;
      setActiveMeters(updatedActiveMeters);
    } else {
      const updatedInactiveMeters = [...inactiveMeters];
      updatedInactiveMeters[index] = value;
      setInactiveMeters(updatedInactiveMeters);
    }
  };

  const handleGoToMeter = (meterid: string) => {
    navigate("/meters", { state: { meterid: meterid } });
  };

  // Location-related functions
  const handleAddRow = () => {
    setSiteIdInput((prevInputs) => [...prevInputs, ""]);
  };

  const handleRemoveRow = (index: number) => {
    setSiteIdInput((prevInputs) => prevInputs.filter((_, i) => i !== index));
  };

  const handleSiteIdInputChange = (value: string, index: number) => {
    setSiteIdInput((prevInputs) => {
      const newInputs = [...prevInputs];
      newInputs[index] = value;
      return newInputs;
    });
  };

  const handleGoToSite = (siteId: string) => {
    setSiteIdInput(siteIdInput);
    navigate("/site", { state: { siteId } });
  };

  const generateUniqueName = () => {
    const randomSuffix = Math.floor(Math.random() * 1000000000);
    const newName = `Site${randomSuffix}`;
    setNames([...names, newName]);
    setCounter(counter + 1);
    return newName;
  };

  const handleSubmit = async (skipNext?: boolean) => {
    // console.log("🚀 AddSite: Starting handleSubmit");
    // console.log("📊 Current siteData:", JSON.stringify(siteData, null, 2));
    // console.log("📊 Current stepUUIDs:", JSON.stringify(stepUUIDs, null, 2));

    // Add validation for required fields
    // if (!siteData?.locationId) {
    //   // console.error("❌ Location ID is missing");
    //   setToast({
    //     message: "Location ID is required",
    //     severity: "error",
    //   });
    //   return;
    // }

    if (!siteData.linkToCustomer) {
      // console.error("❌ Consumer ID is missing");
      setToast({
        message: "Consumer ID is required. Please create a consumer first.",
        severity: "error",
      });
      return;
    }

    // Set loading state
    if (window.setIsLoading) {
      window.setIsLoading(true);
    }

    try {
      const requestData = {
        tenantId: {
          id: "e936e050-e1d7-11ee-8baa-37d11d926d57",
          entityType: "TENANT",
        },
        name: generateUniqueName(),
        type: "SITE",
        label: "NY Building",
        additionalInfo: {
          UseAsLoadProfile: siteData.useAsLoadProfile,
          status: siteData.status ? "Active" : "Inactive", // Store as string
          serviceCommodityType: siteData.serviceCommodityType?.value as string,
          serviceType: siteData.serviceType?.value,
          operatingHours: siteData.operatingHours,
          latitude: siteData.latitude,
          longitude: siteData.longitude,
          activeMeters: activeMeters,
          locationId: siteData.locationId,
          substationName: siteData.substationName,
          zoneNumber: siteData.zoneNumber,
          feederName: siteData.feederName,
          dtrName: siteData.dtrName,
          feederPillarNo: siteData.feederPillarNo,
          meterLocation: siteData.meterLocation,
          changeOfMeterLocation: siteData.changeOfMeterLocation,
          dateOfConnection: siteData.dateOfConnection?.toString(),
          // Location data
          consumerType: siteData.consumerType?.value,
          streetAddress: siteData.streetAddress,
          houseNo: siteData.houseNo,
          state: siteData.state,
          city: siteData.city,
          zip: siteData.zipCode,
          siteIdInput: siteIdInput,
          divisionCode: siteData.divisionCode,
          subDivisionCode: siteData.subDivisionCode,
          numberOfMeters: siteData.numberOfMeters,
        },
        customer_id: siteData.linkToCustomer,
        asset_profile_id: "",
        external_id: "",
        version: 0,
      };

      // console.log(
      //   "📤 Sending site creation request:",
      //   JSON.stringify(requestData, null, 2)
      // );

      let secondResponse = null;
      const response = await fetchPOSTRequest(`asset`, requestData);
      // console.log("📥 Site creation response:", response);

      if (response?.id?.id) {
        // console.log("✅ Site created successfully with ID:", response.id.id);

        // Link site to customer
        // console.log(
        //   `📞 Making customer/asset link API call: customer/${linkToCustomer}/asset/${response.id.id}`
        // );
        secondResponse = await fetchPOSTRequest(
          `customer/${siteData.linkToCustomer}/asset/${response.id.id}`,
          {},
          true,
        );
        // console.log("📥 Customer/asset link response:", secondResponse);

        if (secondResponse?.status === 200) {
          // console.log("✅ Site linked to customer successfully");

          // Update step UUID
          updateStepUUID("site", response.id.id);

          // Update consumer with the new site ID - with validation
          try {
            // console.log("🔄 Updating consumer additionalInfo with site ID");
            // console.log("🔍 Pre-validation check:");
            // console.log("- consumerId:", linkToCustomer);
            // console.log("- assetType: site");
            // console.log("- assetId:", response.id.id);

            // Validate all parameters before calling updateConsumerWithAssetId
            if (
              !siteData.linkToCustomer ||
              siteData.linkToCustomer === "undefined"
            ) {
              throw new Error("Consumer ID is undefined or invalid");
            }
            if (!response.id.id || response.id.id === "undefined") {
              throw new Error("Site ID is undefined or invalid");
            }

            await updateConsumerWithAssetId(
              siteData.linkToCustomer,
              "site",
              response.id.id,
            );
            // console.log("✅ Consumer additionalInfo updated with site ID");
          } catch (updateError: any) {
            // console.error(
            //   "❌ Error updating consumer with site ID:",
            //   updateError
            // );
            console.error("Error details:", {
              consumerId: siteData.linkToCustomer,
              siteId: response.id.id,
              errorMessage: updateError.message,
              errorResponse: updateError.response?.data,
            });
            setToast({
              message:
                "Site created and linked but failed to update consumer info. Please try again.",
              severity: "warning",
            });
          }

          // Reset form and proceed
          setSearchValue({ ...searchValue, value: response.id.id });
          if (!skipNext) {
            setSiteData({
              useAsLoadProfile: false,
              status: true, // true = Active, false = Inactive
              serviceCommodityType: "",
              serviceType: "",
              operatingHours: "",
              latitude: "",
              longitude: "",
              locationId: "",
              substationName: "",
              zoneNumber: "",
              feederName: "",
              dtrName: "",
              feederPillarNo: "",
              meterLocation: "",
              changeOfMeterLocation: "",
              dateOfConnection: new Date(),
              // Location fields
              consumerType: "",
              houseNo: "",
              streetAddress: "",
              city: "",
              state: "",
              zipCode: "",
              divisionCode: "",
              subDivisionCode: "",
              numberOfMeters: "",
              linkToCustomer: "",
            });

            setActiveMeters([]);
            setSiteIdInput([]);
          }

          showStatus(
            "success",
            response.id.id + " Site created and linked successfully!",
          );
          if (!skipNext) {
            next();
          }
        } else {
          console.error("❌ Customer/asset link failed:", secondResponse);
          const errorMessage = extractErrorMessage(secondResponse);
          setToast({
            message:
              errorMessage ||
              "Site created but failed to link to customer. Please try again.",
            severity: "error",
          });
        }
      } else {
        console.error("❌ Site creation failed - no response ID");
        const errorMessage = extractErrorMessage(response);
        setToast({
          message:
            errorMessage || "Site creation failed - no response ID received",
          severity: "error",
        });
      }
    } catch (error: any) {
      console.error("❌ Error during site creation:", error);
      console.error("Error details:", {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
      });

      const errorMessage = extractErrorMessage(error);

      setToast({
        message: errorMessage,
        severity: "error",
      });

      showStatus("error", errorMessage);
    } finally {
      // Reset loading state
      if (window.setIsLoading) {
        window.setIsLoading(false);
      }
    }
  };

  const handleSiteSearch = async (payload?: string) => {
    try {
      let searchTerm = "";

      if (searchValue?.value.trim()) {
        searchTerm = searchValue?.value;
      } else if (payload) {
        searchTerm = payload;
      } else {
        setToast({
          message: "Please enter a valid search value.",
          severity: "error",
        });
        return;
      }

      const response = await fetchGETRequest(`asset/${searchTerm}`, {}, true);

      if (response?.data?.additionalInfo) {
        const {
          useAsLoadProfile,
          status,
          serviceCommodityType,
          serviceType,
          operatingHours,
          latitude,
          longitude,
          activeMeters,
          locationId,
          substationName,
          zoneNumber,
          feederName,
          dtrName,
          feederPillarNo,
          meterLocation,
          changeOfMeterLocation,
          dateOfConnection,
          // Location fields
          consumerType,
          streetAddress,
          houseNo,
          state,
          city,
          zip,
          siteIdInput,
          divisionCode,
          subDivisionCode,
          numberOfMeters,
        } = response?.data?.additionalInfo;
        setSiteData({
          id: response?.data?.id,
          useAsLoadProfile: useAsLoadProfile,
          status: status === "Active" ? true : false, // Convert string to boolean
          operatingHours: operatingHours,
          latitude: latitude,
          longitude: longitude,
          locationId: locationId,
          substationName: substationName,
          zoneNumber: zoneNumber,
          feederName: feederName,
          dtrName: dtrName,
          feederPillarNo: feederPillarNo,
          meterLocation: meterLocation,
          changeOfMeterLocation: changeOfMeterLocation,
          dateOfConnection: dateOfConnection
            ? new Date(dateOfConnection)
            : new Date(),
          serviceCommodityType: commodityTypeOptions.find(
            (item) => item.value === serviceCommodityType,
          ),
          serviceType: serviceTypeOptions.find(
            (item) => item.value === serviceType,
          ),
          // Location fields
          consumerType: consumerTypeOptions.find(
            (item) => item.value === consumerType,
          ),
          streetAddress: streetAddress,
          houseNo: houseNo,
          state: state,
          city: city,
          zipCode: zip,
          divisionCode: divisionCode,
          subDivisionCode: subDivisionCode,
          numberOfMeters: numberOfMeters,
          linkToCustomer: response?.data?.customerId?.id,
        });
        setSiteIdInput(siteIdInput || []);
        setActiveMeters(activeMeters);
      } else {
        setToast({ message: "Site not found", severity: "error" });
      }
    } catch (error: any) {
      console.error("Error fetching consumer:", error.message);
    }
  };

  const getCustomFields = (item: any) => {
    switch (item?.type?.label) {
      case "Date":
        return (
          <DatePickerInput
            label={item?.title}
            format={"dd/MM/yyyy"}
            placeholder={"dd/mm/yyyy"}
            value={siteData?.[item?.title?.toUpperCase()] || null}
            onChange={(event) => {
              setSiteData({
                ...siteData,
                [item?.title?.toUpperCase()]: event,
              });
            }}
          />
        );
      case "Checkbox":
        return (
          <Stack mt={"30px"}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={siteData?.[item?.title?.toUpperCase()] || false}
                  onChange={(event) => {
                    setSiteData({
                      ...siteData,
                      [item?.title?.toUpperCase()]: event.target.checked,
                    });
                  }}
                  sx={{
                    color: "#1976d2",
                    "&.Mui-checked": {
                      color: "#1976d2",
                    },
                  }}
                />
              }
              label={item?.title}
            />
          </Stack>
        );
      case "Text":
        return (
          <>
            <CustomInput
              label={item?.title}
              placeholder={item?.title}
              type="text"
              value={siteData?.[item?.title?.toUpperCase()] || ""}
              onChange={(event) => {
                setSiteData({
                  ...siteData,
                  [item?.title?.toUpperCase()]: event.target.value,
                });
              }}
            />
          </>
        );

      case "Number":
        return (
          <>
            <CustomInput
              label={item?.title}
              placeholder={item?.title}
              type="number"
              value={siteData?.[item?.title?.toUpperCase()] || ""}
              onChange={(event) => {
                setSiteData({
                  ...siteData,
                  [item?.title?.toUpperCase()]: event.target.value,
                });
              }}
            />
          </>
        );

      case "Dropdown":
        return (
          <>
            <CustomOutlinedSelect
              label={item?.title}
              placeholder={item?.title}
              options={item?.options}
              value={siteData?.[item?.title?.toUpperCase()] || ""}
              onChange={(event) => {
                setSiteData({
                  ...siteData,
                  [item?.title?.toUpperCase()]: event,
                });
              }}
            />
          </>
        );

      default:
        return null;
    }
  };

  useEffect(() => {
    setSearchValue({ ...searchValue, value: id || "" });
  }, [id]);

  useEffect(() => {
    if (location?.state?.siteId) {
      setSearchValue({ ...searchValue, value: location?.state?.siteId });
      handleSiteSearch(location?.state?.siteId);
    }
  }, [location]);

  // Expose the save function to the global scope for the footer save button
  useEffect(() => {
    window.handleSubmitSite = (skipNext?: boolean) => handleSubmit(skipNext);
    return () => {
      delete window.handleSubmitSite;
    };
  }, [siteData, activeMeters, siteIdInput]);

  // Auto-fill consumer ID from previous step
  useEffect(() => {
    // console.log("🔄 AddSite: Auto-fill consumer ID effect triggered");
    // console.log("- stepUUIDs.consumer:", stepUUIDs.consumer);
    // console.log("- current linkToCustomer:", linkToCustomer);

    if (stepUUIDs.consumer && !siteData.linkToCustomer) {
      // console.log(
      //   "✅ Setting linkToCustomer from stepUUIDs.consumer:",
      //   stepUUIDs.consumer
      // );
      setSiteData((prevState) => ({
        ...prevState,
        linkToCustomer: stepUUIDs.consumer,
      }));
    } else if (!stepUUIDs.consumer) {
      // console.warn("⚠️ stepUUIDs.consumer is not available");
    } else if (siteData.linkToCustomer) {
      // console.log("ℹ️ linkToCustomer already set:", linkToCustomer);
    }
  }, [stepUUIDs.consumer, siteData.linkToCustomer]);

  // Additional validation effect to ensure linkToCustomer is set
  useEffect(() => {
    if (stepUUIDs.consumer && siteData.linkToCustomer !== stepUUIDs.consumer) {
      // console.log("🔄 Syncing linkToCustomer with stepUUIDs.consumer");
      // console.log("- stepUUIDs.consumer:", stepUUIDs.consumer);
      // console.log("- current linkToCustomer:", linkToCustomer);
      setSiteData((prevState) => ({
        ...prevState,
        linkToCustomer: stepUUIDs.consumer,
      }));
    }
  }, [stepUUIDs.consumer]);

  const dynamicTypeSpecificFields = useMemo(() => {
    return getTypeSpecificConsumerFields("site");
  }, []);

  const dynamicTypeSpecificFieldsLocation = useMemo(() => {
    return getTypeSpecificConsumerFields("location");
  }, []);

  return (
    <Box>
      <Card sx={{ mt: "20px" }}>
        <TabContext value={activeTab}>
          <Box
            sx={{
              borderBottom: 1,
              borderColor: "#CACDD7",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <TabList
              onChange={(_, newValue) => setActiveTab(newValue)}
              scrollButtons={false}
              TabIndicatorProps={{
                style: {
                  backgroundColor: "##1280E7",
                  height: "3px",
                },
              }}
            >
              {TABS.map((item) => {
                return (
                  <Tab
                    label={
                      <Box
                        display="flex"
                        alignItems="center"
                        gap={"7px"}
                        fontWeight={500}
                      >
                        {item}
                      </Box>
                    }
                    value={item}
                    className="consumer_right_card_list_tab"
                  />
                );
              })}
            </TabList>

            <ContainedButton
              label={"Create custom fields"}
              sx={{
                width: "fit-content",
                fontWeight: 600,
                color: "#ffffff",
              }}
              onClick={() => toggleCustomFieldDrawer?.("Site", true)}
            />
          </Box>

          <CardContent>
            <TabPanel value={TABS[0]} sx={{ padding: 0 }}>
              <Typography variant="h6" fontWeight={600} mb={2}>
                Location Details
              </Typography>
              <Grid container spacing={2}>
                {dynamicTypeSpecificFieldsLocation.map((field) => (
                  <Grid
                    key={field.name}
                    size={
                      field.fullWidth ? { xs: 12 } : { xs: 12, md: 4, lg: 4 }
                    }
                    mt={"10px"}
                  >
                    {renderTypeSpecificField(field, siteData, setSiteData)}
                  </Grid>
                ))}
              </Grid>

              {/* Location Details Section */}
              <Stack sx={{ paddingTop: "30px", paddingBottom: "20px" }}>
                <Divider sx={{ borderWidth: "1.5px" }} />
              </Stack>
              <Typography variant="h6" fontWeight={600} mb={2}>
                Site Details
              </Typography>
              <Grid container spacing={2} mt={"10px"}>
                {dynamicTypeSpecificFields.map((field) => (
                  <Grid
                    key={field.name}
                    size={
                      field.fullWidth ? { xs: 12 } : { xs: 12, md: 4, lg: 4 }
                    }
                    mt={"10px"}
                  >
                    {renderTypeSpecificField(field, siteData, setSiteData)}
                  </Grid>
                ))}
              </Grid>

              {createdFields?.["Location"] && (
                <>
                  <Stack sx={{ paddingTop: "30px", paddingBottom: "20px" }}>
                    <Divider sx={{ borderWidth: "1.5px" }} />
                  </Stack>

                  <Grid container spacing={2}>
                    {createdFields?.["Location"]?.map((item: any) => {
                      return (
                        <Grid size={{ xs: 12, md: 4, lg: 4 }} mt={"10px"}>
                          {getCustomFields(item)}
                        </Grid>
                      );
                    })}
                  </Grid>
                </>
              )}

              {createdFields?.["Site"] && (
                <>
                  <Stack sx={{ paddingTop: "30px", paddingBottom: "20px" }}>
                    <Divider sx={{ borderWidth: "1.5px" }} />
                  </Stack>

                  <Grid container spacing={2}>
                    {createdFields?.["Site"]?.map((item: any) => {
                      return (
                        <Grid size={{ xs: 12, md: 4, lg: 4 }} mt={"10px"}>
                          {getCustomFields(item)}
                        </Grid>
                      );
                    })}
                  </Grid>
                </>
              )}
            </TabPanel>

            <TabPanel value={TABS[1]} sx={{ padding: 0 }}>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, md: 4, lg: 4 }}>
                  <CustomInput
                    label={"Latitude"}
                    placeholder={"Latitude"}
                    name={"latitude"}
                    value={siteData?.latitude}
                    onChange={(e) =>
                      setSiteData({
                        ...siteData,
                        latitude: e.target.value,
                      })
                    }
                  />
                </Grid>

                <Grid size={{ xs: 12, md: 4, lg: 4 }}>
                  <CustomInput
                    label={"Longitude"}
                    placeholder={"Longitude"}
                    name={"longitude"}
                    onChange={(e) =>
                      setSiteData({
                        ...siteData,
                        longitude: e.target.value,
                      })
                    }
                    value={siteData?.longitude}
                  />
                </Grid>
              </Grid>
            </TabPanel>

            <TabPanel value={TABS[2]} sx={{ padding: 0 }}>
              <Box display={"flex"} gap={"20px"}>
                <Stack width={"60%"}>
                  {activeMeters?.map((item, index) => (
                    <>
                      <Stack
                        key={index}
                        direction={"row"}
                        gap={"20px"}
                        alignItems={"center"}
                        sx={{ marginBottom: "16px" }}
                      >
                        <Typography variant="body1" fontWeight={600}>
                          Meter ID:
                        </Typography>

                        <CustomSearch
                          placeholder={"10 digits"}
                          sx={{
                            width: "400px",
                            height: "36px",
                            borderRadius: "8px",
                            border: "1px solid #c3c3c3",
                            "& .custom_search_input": {
                              padding: "4px 10px",
                              fontSize: "14px",
                            },
                          }}
                          value={item}
                          onChange={(e) => handleMeterIdChange(e, index, true)}
                          onClick={() => handleGoToMeter(item)}
                        />

                        <Stack direction={"row"} gap={"10px"}>
                          <ContainedButton
                            label={"Go to Meter"}
                            sx={{
                              width: "fit-content",
                              fontWeight: 600,
                              height: "35px",
                              color: "#ffffff",
                            }}
                            onClick={() => handleGoToMeter(item)}
                          />

                          <Box
                            sx={{
                              backgroundColor: "#FA6B6B",
                              width: "35px",
                              height: "35px",
                              borderRadius: "4px",
                              display: "flex",
                              justifyContent: "center",
                              alignItems: "center",
                            }}
                            onClick={() => removeRow(index)}
                          >
                            <UnifiedIcon
                              icon={FaRegTrashAlt}
                              color="#ffffff"
                              style={{ marginBottom: "2px" }}
                            />
                          </Box>
                        </Stack>
                      </Stack>
                    </>
                  ))}
                </Stack>
              </Box>

              <ContainedButton
                label={"Add Row"}
                sx={{
                  width: "fit-content",
                  fontWeight: 600,
                  color: "#ffffff",
                }}
                onClick={addRow}
              />
            </TabPanel>

            <Stack
              direction={"row"}
              justifyContent={"space-between"}
              justifyItems={"center"}
              mt={"20px"}
            >
              {/* <ContainedButton
                label={"Install Meter"}
                
                sx={{
                  width: "fit-content",
                  fontWeight: 600,
                  height: "35px",
                  color: "#ffffff",
                }}
                onClick={() => alert("coming soon...")}
              /> */}
            </Stack>
          </CardContent>
        </TabContext>
      </Card>

      {/* Save Button for Site */}
      {/* <Box sx={{ mt: 2, display: "flex", justifyContent: "flex-end" }}>
        <ContainedButton
          label={"Save Site"}
          
          sx={{
            width: "fit-content",
            fontWeight: 600,
            color: "#ffffff",
          }}
          onClick={handleSubmit}
        />
      </Box> */}
    </Box>
  );
};

const AddContract = ({
  id,
  setToast,
  showStatus,
  activeStep,
  next,
  back,
  updateStepUUID,
  stepUUIDs,
  saveStepData,
  loadStepData,
  shouldStartFresh,
  setShouldStartFresh,
  updateConsumerWithAssetId,
}: AddContractProps) => {
  const [searchValue, setSearchValue] = useState({
    type: "id",
    value: "",
  });
  const [searchOpen, setSearchOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("Contract Details");
  const [dropdownOptions, setDropdownOptions] = useState<{
    registerRuleBook: {
      label: string;
      value: string;
    }[];
    subscribedTariff: {
      label: string;
      value: string;
    }[];
  }>({
    registerRuleBook: [
      {
        label: "TEST_RULE_BOOK2",
        value: "044cb970-133b-11ef-a093-1127a7ee82a3",
      },
      {
        label: "TEST_RULE_BOOK1",
        value: "348958b0-12bd-11ef-a093-1127a7ee82a3",
      },
      {
        label: "TEST_RULE_BOOK1",
        value: "64bcc530-12b8-11ef-a093-1127a7ee82a3",
      },
      {
        label: "TEST_RULE_BOOK1",
        value: "c3967710-12c5-11ef-a093-1127a7ee82a3",
      },
    ],
    subscribedTariff: [],
  });
  const [names, setNames] = useState<string[]>([]);
  const [filteredAssets, setFilteredAssets] = useState<any[]>([]);

  const fetchTariffsData = async () => {
    try {
      const response = await getTariffsV2();
      if (response?.success) {
        const formattedTariffs = response.data.map((t) => ({
          label: `${t.name} (v${t.version})`,
          value: t.name,
        }));
        setDropdownOptions((prev) => ({
          ...prev,
          subscribedTariff: formattedTariffs,
        }));
      }
    } catch (error) {
      console.error("Error fetching tariffs data:", error);
    }
  };

  useEffect(() => {
    fetchTariffsData();
  }, []);

  const [contractData, setContractData] = useState<{
    id: { id: string; entityType: string };
    consumerId: string;
    siteId: string;
    status: boolean;
    startDate: Date;
    endDate: Date;
    commodityType: any;
    commodityCategory: any;
    registerRuleBook: any;
    subscribedVoltage: string;
    subscribedTariff: any;
    billingCycle: string;
    producerPremise: string;
    [key: string]: any;
  }>({
    id: { id: "", entityType: "" },
    consumerId: "",
    siteId: "",
    status: true, // true = Active, false = Inactive
    startDate: new Date(),
    endDate: new Date(),
    commodityType: "",
    commodityCategory: "",
    registerRuleBook: "",
    subscribedVoltage: "",
    subscribedTariff: "",
    billingCycle: "",
    producerPremise: "",
  });
  const [billingData, setBillingData] = useState({
    fromDate: new Date(),
    toDate: new Date(),
    billingStatus: "",
  });
  const { toggleCustomFieldDrawer, createdFields } = useCustomFields();

  const location = useLocation();

  const resetFormData = () => {
    setContractData({
      id: { id: "", entityType: "" },
      consumerId: "",
      siteId: "",
      status: true, // true = Active, false = Inactive
      startDate: new Date(),
      endDate: new Date(),
      commodityType: "",
      commodityCategory: "",
      registerRuleBook: "",
      subscribedVoltage: "",
      subscribedTariff: "",
      billingCycle: "",
      producerPremise: "",
    });
    setBillingData({
      fromDate: new Date(),
      toDate: new Date(),
      billingStatus: "",
    });
    setFilteredAssets([]);
    setActiveTab("Contract Details");
  };

  // Handle start fresh logic
  useEffect(() => {
    if (shouldStartFresh) {
      resetFormData();
      setShouldStartFresh(false);
    }
  }, [shouldStartFresh, setShouldStartFresh]);

  // Load persisted data on component mount
  useEffect(() => {
    if (!shouldStartFresh) {
      const persistedData = loadStepData(2);
      if (persistedData) {
        setContractData(persistedData.contractData || contractData);
        setBillingData(persistedData.billingData || billingData);
        setFilteredAssets(persistedData.filteredAssets || []);
        setActiveTab(persistedData.activeTab || "Contract Details");
      }
    }
  }, [shouldStartFresh]);

  // Save data whenever form state changes
  useEffect(() => {
    if (!shouldStartFresh) {
      const dataToSave = {
        contractData,
        billingData,
        filteredAssets,
        activeTab,
      };
      saveStepData(2, dataToSave);
    }
  }, [contractData, billingData, filteredAssets, activeTab, shouldStartFresh]);

  const startDate = moment(contractData?.startDate);
  const today = moment();
  const isActive = startDate.isBefore(today);

  const navigate = useNavigate();
  const TABS = ["Contract Details", "Billing Determinants"];

  const determinantStatusOptions = [
    { value: "Initial", label: "Initial" },
    { value: "Success", label: "Success" },
    { value: "Rejected", label: "Rejected" },
  ];

  const commodityTypeOptions = [
    { value: "Electricity", label: "Electricity" },
    { value: "Water", label: "Water" },
    { value: "Gas", label: "Gas" },
  ];

  const commodityCategoryOptions = [
    { value: "Residential", label: "Residential" },
    { value: "Commercial", label: "Commercial" },
  ];

  const generateUniqueName = () => {
    const randomSuffix = Math.floor(Math.random() * 1000000000);
    const newName = `contract${randomSuffix}`;
    return newName;
  };

  const handleSubmit = async (skipNext?: boolean) => {
    // console.log("🚀 AddContract: Starting handleSubmit");
    // console.log(
    //   "📊 Current contractData:",
    //   JSON.stringify(contractData, null, 2)
    // );
    // console.log("📊 Current stepUUIDs:", JSON.stringify(stepUUIDs, null, 2));

    // Add validation for required fields
    if (!contractData?.consumerId) {
      console.error("❌ Consumer ID is missing");
      setToast({
        message: "Consumer ID is required. Please create a consumer first.",
        severity: "error",
      });
      return;
    }

    // Set loading state
    if (window.setIsLoading) {
      window.setIsLoading(true);
    }

    try {
      const requestData = {
        type: "CONTRACT",
        tenantId: {
          id: "e936e050-e1d7-11ee-8baa-37d11d926d57",
          entityType: "TENANT",
        },
        name: generateUniqueName(),
        label: "NY Building",
        additionalInfo: {
          consumerid: contractData.consumerId,
          siteId: contractData.siteId,
          status: contractData.status ? "Active" : "Inactive", // Store as string
          startdate: contractData.startDate?.toString(),
          enddate: contractData.endDate?.toString(),
          commodityType: contractData.commodityType?.value,
          commodityCategory: contractData.commodityCategory?.value,
          registerRuleBook: contractData.registerRuleBook?.value,
          subscribedVoltage: contractData.subscribedVoltage,
          subscribedTariff: contractData.subscribedTariff?.value,
          billingCycle: contractData.billingCycle,
          producerPremise: contractData.producerPremise,
        },
        customer_id: contractData.consumerId,
        asset_profile_id: "",
        external_id: "",
        version: 0,
      };

      // console.log(
      //   "📤 Sending contract creation request:",
      //   JSON.stringify(requestData, null, 2)
      // );

      let secondResponse = null;
      const response = await fetchPOSTRequest(`asset`, requestData);
      // console.log("📥 Contract creation response:", response);

      if (response?.id?.id) {
        // console.log(
        //   "✅ Contract created successfully with ID:",
        //   response.id.id
        // );

        // Link contract to customer
        // console.log(
        //   `📞 Making customer/asset link API call: customer/${contractData.consumerId}/asset/${response.id.id}`
        // );
        secondResponse = await fetchPOSTRequest(
          `customer/${contractData.consumerId}/asset/${response.id.id}`,
          {},
          true,
        );
        // console.log("📥 Customer/asset link response:", secondResponse);

        if (secondResponse?.status === 200) {
          // console.log("✅ Contract linked to customer successfully");

          // Update step UUID
          updateStepUUID("contract", response.id.id);

          // Update consumer with the new contract ID - with validation
          try {
            // console.log("🔄 Updating consumer additionalInfo with contract ID");
            // console.log("🔍 Pre-validation check:");
            // console.log("- consumerId:", contractData.consumerId);
            // console.log("- assetType: contract");
            // console.log("- assetId:", response.id.id);

            // Validate all parameters before calling updateConsumerWithAssetId
            if (
              !contractData.consumerId ||
              contractData.consumerId === "undefined"
            ) {
              throw new Error("Consumer ID is undefined or invalid");
            }
            if (!response.id.id || response.id.id === "undefined") {
              throw new Error("Contract ID is undefined or invalid");
            }

            await updateConsumerWithAssetId(
              contractData.consumerId,
              "contract",
              response.id.id,
            );
            // console.log("✅ Consumer additionalInfo updated with contract ID");
          } catch (updateError) {
            // console.error(
            //   "❌ Error updating consumer with contract ID:",
            //   updateError
            // );
            // console.error("Error details:", {
            //   consumerId: contractData.consumerId,
            //   contractId: response.id.id,
            //   errorMessage: updateError.message,
            //   errorResponse: updateError.response?.data,
            // });
            setToast({
              message:
                "Contract created and linked but failed to update consumer info. Please try again.",
              severity: "warning",
            });
          }

          // Reset form
          setSearchValue({ ...searchValue, value: response.id.id });
          if (!skipNext) {
            setContractData({
              id: { id: "", entityType: "" },
              consumerId: "",
              siteId: "",
              status: true, // true = Active, false = Inactive
              startDate: new Date(),
              endDate: new Date(),
              commodityType: "",
              commodityCategory: "",
              registerRuleBook: "",
              subscribedVoltage: "",
              subscribedTariff: "",
              billingCycle: "",
              producerPremise: "",
            });
          }

          showStatus(
            "success",
            response.id.id + " Contract created and linked successfully!",
          );
          if (!skipNext) {
            next();
          }
        } else {
          console.error("❌ Customer/asset link failed:", secondResponse);
          const errorMessage = extractErrorMessage(secondResponse);
          setToast({
            message:
              errorMessage ||
              "Contract created but failed to link to customer. Please try again.",
            severity: "error",
          });
        }
      } else {
        console.error("❌ Contract creation failed - no response ID");
        const errorMessage = extractErrorMessage(response);
        setToast({
          message:
            errorMessage ||
            "Contract creation failed - no response ID received",
          severity: "error",
        });
      }
    } catch (error: any) {
      console.error("❌ Error during contract creation:", error);
      console.error("Error details:", {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
      });

      const errorMessage = extractErrorMessage(error);

      setToast({
        message: errorMessage,
        severity: "error",
      });

      showStatus("error", errorMessage);
    } finally {
      // Reset loading state
      if (window.setIsLoading) {
        window.setIsLoading(false);
      }
    }
  };

  const handleContractSearch = async (payload?: string) => {
    try {
      let endpoint;
      if (searchValue?.value.trim()) {
        endpoint = searchValue?.value;
      } else if (payload) {
        endpoint = payload;
      } else {
        setToast({
          message: "Please enter a valid search value.",
          severity: "error",
        });
        return;
      }

      const response = await fetchGETRequest(`asset/${endpoint}`, {}, true);

      if (response?.data?.additionalInfo) {
        const {
          commodityCategory,
          commodityType,
          consumerid,
          enddate,
          registerRuleBook,
          siteId,
          startdate,
          status,
          subscribedVoltage,
          subscribedTariff,
          billingCycle,
          producerPremise,
        } = response?.data?.additionalInfo;

        setContractData({
          id: response?.data?.id,
          consumerId: consumerid,
          siteId: siteId,
          status: status === "Active" ? true : false, // Convert string to boolean
          startDate: startdate ? new Date(startdate) : new Date(),
          endDate: enddate ? new Date(enddate) : new Date(),
          commodityType: commodityTypeOptions.find(
            (item) => item.value === commodityType,
          ),
          commodityCategory: commodityCategoryOptions.find(
            (item) => item.value === commodityCategory,
          ),
          registerRuleBook: dropdownOptions.registerRuleBook.find(
            (item) => item.value === registerRuleBook,
          ),
          subscribedVoltage: subscribedVoltage,
          subscribedTariff: subscribedTariff,
          billingCycle: billingCycle,
          producerPremise: producerPremise,
        });
      } else {
        setToast({ message: "Contract not found", severity: "error" });
      }
    } catch (error: any) {
      console.error("Error fetching consumer:", error.message);
    }
  };
  // Expose the save function to the global scope for the footer save button
  useEffect(() => {
    window.handleSubmitContract = (skipNext?: boolean) =>
      handleSubmit(skipNext);
    return () => {
      delete window.handleSubmitContract;
    };
  }, [contractData]);

  // Auto-fill consumer ID and site ID from previous steps
  useEffect(() => {
    if (stepUUIDs.consumer && !contractData.consumerId) {
      setContractData((prev) => ({
        ...prev,
        consumerId: stepUUIDs.consumer,
      }));
    }
    if (stepUUIDs.site && !contractData.siteId) {
      setContractData((prev) => ({
        ...prev,
        siteId: stepUUIDs.site,
      }));
    }
  }, [
    stepUUIDs.consumer,
    stepUUIDs.site,
    contractData.consumerId,
    contractData.siteId,
  ]);

  const dynamicTypeSpecificFields = useMemo(() => {
    const fields = getTypeSpecificConsumerFields("contract");
    const enrichedFields = fields.map((field) => {
      if (field.type === "select") {
        if (dropdownOptions[field.name as keyof typeof dropdownOptions]) {
          return {
            ...field,
            options:
              dropdownOptions[field.name as keyof typeof dropdownOptions],
          };
        }
      }
      return field;
    });

    return enrichedFields;
  }, [dropdownOptions]);

  const getCustomFields = (item: any) => {
    switch (item?.type?.label) {
      case "Date":
        return (
          <DatePickerInput
            label={item?.title}
            format={"dd/MM/yyyy"}
            placeholder={"dd/mm/yyyy"}
            value={contractData?.[item?.title?.toUpperCase()] || null}
            onChange={(event) => {
              setContractData({
                ...contractData,
                [item?.title?.toUpperCase()]: event,
              });
            }}
          />
        );
      case "Checkbox":
        return (
          <Stack mt={"30px"}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={contractData?.[item?.title?.toUpperCase()] || false}
                  onChange={(event) => {
                    setContractData({
                      ...contractData,
                      [item?.title?.toUpperCase()]: event.target.checked,
                    });
                  }}
                  sx={{
                    color: "#1976d2",
                    "&.Mui-checked": {
                      color: "#1976d2",
                    },
                  }}
                />
              }
              label={item?.title}
            />
          </Stack>
        );
      case "Text":
        return (
          <CustomInput
            label={item?.title}
            placeholder={item?.title}
            type="text"
            value={contractData?.[item?.title?.toUpperCase()] || ""}
            onChange={(event) => {
              setContractData({
                ...contractData,
                [item?.title?.toUpperCase()]: event.target.value,
              });
            }}
          />
        );
      case "Number":
        return (
          <CustomInput
            label={item?.title}
            placeholder={item?.title}
            type="number"
            value={contractData?.[item?.title?.toUpperCase()] || ""}
            onChange={(event) => {
              setContractData({
                ...contractData,
                [item?.title?.toUpperCase()]: event.target.value,
              });
            }}
          />
        );
      case "Dropdown":
        return (
          <CustomOutlinedSelect
            label={item?.title}
            placeholder={item?.title}
            options={item?.options}
            value={contractData?.[item?.title?.toUpperCase()] || ""}
            onChange={(event) => {
              setContractData({
                ...contractData,
                [item?.title?.toUpperCase()]: event,
              });
            }}
          />
        );
      default:
        return null;
    }
  };

  return (
    <Box>
      <Card sx={{ mt: "20px" }}>
        <TabContext value={activeTab}>
          <Box
            sx={{
              borderBottom: 1,
              borderColor: "#CACDD7",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <TabList
              onChange={(_, newValue) => setActiveTab(newValue)}
              scrollButtons={false}
              TabIndicatorProps={{
                style: {
                  backgroundColor: "##1280E7",
                  height: "3px",
                },
              }}
            >
              {TABS.map((item) => {
                return (
                  <Tab
                    label={
                      <Box
                        display="flex"
                        alignItems="center"
                        gap={"7px"}
                        fontWeight={500}
                      >
                        {item}
                      </Box>
                    }
                    value={item}
                    className="consumer_right_card_list_tab"
                  />
                );
              })}
            </TabList>

            <ContainedButton
              label={"Create custom fields"}
              sx={{
                width: "fit-content",
                fontWeight: 600,
                color: "#ffffff",
              }}
              onClick={() => toggleCustomFieldDrawer("Contract", true)}
            />
          </Box>

          <CardContent>
            <TabPanel value={TABS[0]} sx={{ padding: 0 }}>
              <Grid container spacing={2} sx={{ mt: "10px" }}>
                <Grid size={{ xs: 12, md: 4, lg: 4 }} mt={"10px"}>
                  <Typography variant="body1" mb={"10px"}>
                    Consumer ID
                  </Typography>
                  <CustomSearch
                    placeholder={"Consumer ID"}
                    value={contractData?.consumerId}
                    disabled={true}
                    sx={{
                      width: "100%",
                      height: "36px",
                      borderRadius: "8px",
                      border: "1px solid #c3c3c3",
                      backgroundColor: "#f5f5f5",
                      "& .custom_search_input": {
                        padding: "4px 10px",
                        color: "#666",
                        fontSize: "14px",
                      },
                      "& .MuiInputBase-input": {
                        color: "#666",
                        cursor: "not-allowed",
                      },
                    }}
                  />
                </Grid>

                <Grid size={{ xs: 12, md: 4, lg: 4 }} mt={"10px"}>
                  <Typography variant="body1" mb={"10px"}>
                    Site ID
                  </Typography>
                  <CustomSearch
                    placeholder={"Site ID"}
                    value={contractData?.siteId}
                    disabled={true}
                    sx={{
                      width: "100%",
                      height: "36px",
                      borderRadius: "8px",
                      border: "1px solid #c3c3c3",
                      backgroundColor: "#f5f5f5",
                      "& .custom_search_input": {
                        padding: "4px 10px",
                        color: "#666",
                        fontSize: "14px",
                      },
                      "& .MuiInputBase-input": {
                        color: "#666",
                        cursor: "not-allowed",
                      },
                    }}
                  />
                </Grid>

                <Grid size={{ xs: 12, md: 4, lg: 4 }} mt={"10px"}>
                  <Stack mt={"30px"}>
                    <CustomSwitch
                      label={contractData?.status ? "Active" : "Inactive"}
                      value={contractData?.status}
                      onChange={(e: any) => {
                        setContractData({
                          ...contractData,
                          status: e.target.checked,
                        });
                      }}
                    />
                  </Stack>
                </Grid>
              </Grid>

              <Grid container spacing={2} sx={{ mt: "20px" }}>
                {dynamicTypeSpecificFields
                  .filter(
                    (field) =>
                      field.name !== "status" &&
                      field.name !== "consumerId" &&
                      field.name !== "siteId",
                  )
                  .map((field) => (
                    <Grid
                      key={field.name}
                      size={
                        field.fullWidth ? { xs: 12 } : { xs: 12, md: 4, lg: 4 }
                      }
                      mt={"10px"}
                    >
                      {renderTypeSpecificField(
                        field,
                        contractData,
                        setContractData,
                      )}
                    </Grid>
                  ))}
              </Grid>

              {createdFields?.["Contract"] && (
                <>
                  <Stack sx={{ paddingTop: "30px", paddingBottom: "20px" }}>
                    <Divider sx={{ borderWidth: "1.5px" }} />
                  </Stack>

                  <Grid container spacing={2}>
                    {createdFields?.["Contract"]?.map((item: any) => {
                      return (
                        <Grid size={{ xs: 12, md: 4, lg: 4 }} mt={"10px"}>
                          {getCustomFields(item)}
                        </Grid>
                      );
                    })}
                  </Grid>
                </>
              )}
            </TabPanel>

            <TabPanel value={TABS[1]} sx={{ padding: 0 }}>
              <Grid container spacing={2} mt={"20px"}>
                <Grid size={{ xs: 12, md: 4, lg: 4 }}>
                  <DatePickerInput
                    label={"From Date:"}
                    placeholder={"dd/mm/yyyy hh:mm"}
                    name={"fromDate"}
                    value={billingData?.fromDate || ""}
                    onChange={(newValue) => {
                      setBillingData({
                        ...billingData,
                        fromDate: newValue || new Date(),
                      });
                    }}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 4, lg: 4 }}>
                  <DatePickerInput
                    label={"To Date:"}
                    placeholder={"dd/mm/yyyy hh:mm"}
                    name={"toDate"}
                    value={billingData?.toDate || ""}
                    onChange={(newValue) => {
                      setBillingData({
                        ...billingData,
                        toDate: newValue || new Date(),
                      });
                    }}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 4, lg: 4 }}>
                  <CustomOutlinedSelect
                    placeholder={"Status"}
                    options={determinantStatusOptions}
                    name={"billingStatus"}
                    value={billingData?.billingStatus}
                    label={"Billing Determinant Status"}
                    onChange={(newValue) => {
                      setBillingData({
                        ...billingData,
                        billingStatus: (newValue?.value as string) || "",
                      });
                    }}
                  />
                </Grid>
              </Grid>

              <ContainedButton
                label={"Search"}
                sx={{
                  width: "fit-content",
                  fontWeight: 600,
                  mt: "20px",
                  color: "#ffffff",
                }}
                onClick={() => {}}
              />
            </TabPanel>
          </CardContent>
        </TabContext>
      </Card>
    </Box>
  );
};

const AddMeter = ({
  id,
  setToast,
  showStatus,
  handleStatusModalClose,
  setOpenModal,
  activeStep,
  next,
  back,
  updateStepUUID,
  stepUUIDs,
  saveStepData,
  loadStepData,
  onFinalCompletion,
  shouldStartFresh,
  setShouldStartFresh,
  updateConsumerWithAssetId,
}: AddMeterProps) => {
  const [selectedTab, setSelectedTab] = useState("Meters");

  const meterTypeOptions = [
    {
      value: "SLS-E1-DLMS-1P",
      label: "SLS-E1-DLMS-1P",
    },
    {
      value: "SLS-E2-DLMS-3P",
      label: "SLS-E2-DLMS-3P",
    },
    {
      value: "ITRON-G1-CGT-01",
      label: "ITRON-G1-CGT-01",
    },
    {
      value: "HW-G3-U16",
      label: "HW-G3-U16",
    },
    {
      value: "electricity",
      label: "Electricity",
      typeId: METER_TYPE_IDS.electricity,
    },
    {
      value: "water",
      label: "Water",
      typeId: METER_TYPE_IDS.water,
    },
    {
      value: "gas",
      label: "Gas",
      typeId: METER_TYPE_IDS.gas,
    },
  ];
  const [counter, setCounter] = useState(1);
  const [names, setNames] = useState<string[]>([]);
  const [readClusterDetail, setReadClusterDetail] = useState({
    startDate: new Date(),
    endDate: new Date(),
    registerTypeCode: { label: "KWH", value: "IP_KWH" },
  });
  const navigate = useNavigate();
  const { toggleCustomFieldDrawer, createdFields, setCreatedFields } =
    useCustomFields();
  const location = useLocation();
  const [dynamicTypeSpecificFields, setDynamicTypeSpecificFields] = useState<
    any[]
  >([]);
  const [dropdownOptions, setDropdownOptions] = useState<{
    meterType: { label: string; value: string }[];
    assignToCustomer: { label: string; value: string }[];
  }>({
    meterType: [
      // { label: "MeterTime", value: "IP_MeterTime" },
      // { label: "Current", value: "IP_Current" },
      // { label: "Voltage", value: "IP_Voltage" },
      // { label: "Frequency", value: "IP_Frequency" },
      // { label: "NeutralCurrent", value: "IP_NeutralCurrent" },
      // { label: "PowerFactor", value: "IP_PowerFactor" },
      // { label: "KW", value: "IP_KW" },
      // { label: "KVA", value: "IP_KVA" },
      // { label: "KWH", value: "IP_KWH" },
      // { label: "KWHEXP", value: "IP_KWHEXP" },
      // { label: "KVAH", value: "IP_KVAH" },
      // { label: "KVAHEXP", value: "IP_KVAHEXP" },
      // { label: "MDKW", value: "IP_MDKW" },
      // { label: "MDKVA", value: "IP_MDKVA" },
      // { label: "MDResetCount", value: "IP_MDResetCount" },
      // { label: "ProgramCount", value: "IP_ProgramCount" },
      // { label: "TamperCount", value: "IP_TamperCount" },
      // { label: "TotalPowerOnDuration", value: "IP_TotalPowerOnDuration" },
      // { label: "RelayStatus", value: "IP_RelayStatus" },
      // { label: "LoadLimitValue", value: "IP_LoadLimitValue" },
      { label: "SES-RES-SERIES", value: "SES-RES-SERIES" },
      { label: "SES-COM-SERIES", value: "SES-COM-SERIES" },
      { label: "SES-RES-INTERVAL", value: "SES-RES-INTERVAL" },
      { label: "SES-COM-INTERVAL", value: "SES-COM-INTERVAL" },
      { label: "ITR-RES-SERIES", value: "ITR-RES-SERIES" },
      { label: "ITR-COM-SERIES", value: "ITR-COM-SERIES" },
      { label: "ITR-RES-INTERVAL", value: "ITR-RES-INTERVAL" },
      { label: "ITR-COM-INTERVAL", value: "ITR-COM-INTERVAL" },
    ],
    assignToCustomer: [],
  });

  const [meterInfo, setMeterInfo] = useState<
    {
      id: { id: string; entityType: string };
      manufacturer: { value: string; label: string };
      meterType: { value: string; label: string };
      meterSerialNo: string;
      meterBadgeNo: string;
      meterInstallDate: Date | null;
      useAsLoadProfile: boolean;
      status: string;
      assignToCustomer: { value: string; label: string } | undefined;
      siteId: string;
      existingMeterNumber: string;
      lineLtCtRatio: string;
      meterBoxDimensions: string;
      meterLegalStatus: string;
      remarks: string;
    } & { [key: string]: any }
  >({
    id: { id: "", entityType: "" },
    manufacturer: { value: "", label: "" },
    meterType: { value: "", label: "" },
    meterSerialNo: "",
    meterBadgeNo: "",
    meterInstallDate: new Date(),
    useAsLoadProfile: false,
    status: "Inactive",
    assignToCustomer: undefined,
    siteId: "",
    existingMeterNumber: "",
    lineLtCtRatio: "",
    meterBoxDimensions: "",
    meterLegalStatus: "",
    remarks: "",
    connectionStatus: true,
    assignExistingMeter: false,
  });

  const { customFieldId } = useAppSelector((state) => state.auth);

  const resetFormData = () => {
    setMeterInfo({
      id: { id: "", entityType: "" },
      manufacturer: { value: "", label: "" },
      meterType: { value: "", label: "" },
      meterSerialNo: "",
      meterBadgeNo: "",
      meterInstallDate: new Date(),
      useAsLoadProfile: false,
      status: "Inactive",
      assignToCustomer: { value: "", label: "" },
      siteId: "",
      existingMeterNumber: "",
      lineLtCtRatio: "",
      meterBoxDimensions: "",
      meterLegalStatus: "",
      remarks: "",
      connectionStatus: true,
      assignExistingMeter: false,
    });
    setReadClusterDetail({
      startDate: new Date(),
      endDate: new Date(),
      registerTypeCode: { label: "KWH", value: "IP_KWH" },
    });
    setDynamicTypeSpecificFields([]);
    setSelectedTab("Meters");
  };

  // Handle start fresh logic
  useEffect(() => {
    if (shouldStartFresh) {
      resetFormData();
      setShouldStartFresh(false);
    }
  }, [shouldStartFresh, setShouldStartFresh]);

  // Load persisted data on component mount
  useEffect(() => {
    if (!shouldStartFresh) {
      const persistedData = loadStepData(3);
      if (persistedData) {
        setMeterInfo(persistedData.meterInfo || meterInfo);
        setReadClusterDetail(
          persistedData.readClusterDetail || readClusterDetail,
        );
        setDynamicTypeSpecificFields(
          persistedData.dynamicTypeSpecificFields || [],
        );
        setSelectedTab(persistedData.selectedTab || "Meters");
      }
    }
  }, [shouldStartFresh]);

  // Save data whenever form state changes
  useEffect(() => {
    if (!shouldStartFresh) {
      const dataToSave = {
        meterInfo,
        readClusterDetail,
        dynamicTypeSpecificFields,
        selectedTab,
      };
      saveStepData(3, dataToSave);
    }
  }, [
    meterInfo,
    readClusterDetail,
    dynamicTypeSpecificFields,
    selectedTab,
    shouldStartFresh,
  ]);

  const Manufacturer_options = [
    { value: "SENSUS", label: "SENSUS" },
    { value: "ITRON", label: "ITRON" },
  ];

  const TABS = ["Meters", "Read Cluster"];

  const handleResetMeterInfo = () => {
    setMeterInfo({
      manufacturer: { value: "", label: "" },
      meterType: { value: "", label: "" },
      meterSerialNo: "",
      meterBadgeNo: "",
      meterInstallDate: new Date(),
      useAsLoadProfile: false,
      status: "Inactive",
      assignToCustomer: { value: "", label: "" },
      existingMeterNumber: "",
      lineLtCtRatio: "",
      meterBoxDimensions: "",
      meterLegalStatus: "",
      remarks: "",
      id: { id: "", entityType: "" },
      siteId: "",
    });
  };

  // Handle meter type change to load type-specific fields
  const handleMeterTypeChange = async (selectedMeterType: any) => {
    setMeterInfo({
      ...meterInfo,
      meterType: selectedMeterType,
    });

    if (selectedMeterType?.typeId) {
      try {
        // Fetch type-specific data from asset endpoint using typeId
        const response = await fetchGETRequest(
          `asset/${selectedMeterType.typeId}`,
        );

        if (response?.additionalInfo) {
          const typeSpecificFields = getTypeSpecificFields(
            selectedMeterType.value.toLowerCase(),
          );

          // Update meterInfo with fetched data and set type-specific fields
          const updatedMeterInfo = {
            ...meterInfo,
            meterType: selectedMeterType,
          };

          typeSpecificFields.forEach((field) => {
            if (response.additionalInfo.hasOwnProperty(field.name)) {
              const apiValue = response.additionalInfo[field.name];
              if (field.type === "dropdown" && field.options) {
                (updatedMeterInfo as any)[field.name] =
                  field.options.find((opt: any) => opt.value === apiValue) ||
                  null;
              } else if (field.type === "date" && apiValue) {
                (updatedMeterInfo as any)[field.name] = new Date(
                  apiValue as string,
                );
              } else {
                (updatedMeterInfo as any)[field.name] = apiValue;
              }
            }
          });

          setMeterInfo(updatedMeterInfo);
          setDynamicTypeSpecificFields(typeSpecificFields);
        } else {
          // If no API data, just set the fields without defaults
          const typeSpecificFields = getTypeSpecificFields(
            selectedMeterType.value.toLowerCase(),
          );
          setDynamicTypeSpecificFields(typeSpecificFields);
        }
      } catch (error) {
        console.error("Error fetching meter type data:", error);
        // If error, just set the fields without defaults
        const typeSpecificFields = getTypeSpecificFields(
          selectedMeterType.value.toLowerCase(),
        );
        setDynamicTypeSpecificFields(typeSpecificFields);
      }
    } else {
      // For existing meter types without typeId, just set type-specific fields
      if (selectedMeterType?.value) {
        const typeSpecificFields = getTypeSpecificFields(
          selectedMeterType.value.toLowerCase(),
        );
        setDynamicTypeSpecificFields(typeSpecificFields);
      } else {
        setDynamicTypeSpecificFields([]);
      }
    }
  };

  const getPopulatedDefaultFields = () => {
    return DEFAULT_METER_FIELDS.map((field) => {
      if (field.name === "meterType") {
        return {
          ...field,
          options: meterTypeOptions,
          onChange: handleMeterTypeChange,
        };
      } else if (field.name === "assignToCustomer") {
        return {
          ...field,
          options: dropdownOptions.assignToCustomer,
        };
      } else if (field.name === "manufacturer") {
        return {
          ...field,
          options: Manufacturer_options,
        };
      }
      return field;
    });
  };

  const generateUniqueName = React.useCallback(() => {
    const newName = `meter${counter}`;
    setNames([...names, newName]);
    setCounter(Math.random());
    return newName;
  }, [counter, names, setCounter, setNames]);

  const handleSave = React.useCallback(
    async (skipNext?: boolean) => {
      console.log(
        "DEBUG: handleSave called. existingMeterNumber:",
        meterInfo.existingMeterNumber,
      );
      console.log("DEBUG: assignExistingMeter:", meterInfo.assignExistingMeter);

      // Validation
      if (meterInfo.assignExistingMeter) {
        if (!meterInfo.existingMeterNumber.trim()) {
          console.warn(
            "DEBUG: Validation failed - existingMeterNumber is empty",
          );
          setToast({
            message: "Please enter an Existing Meter ID",
            severity: "error",
          });
          return;
        }
      } else {
        // Dynamic validation based on JSON configuration for NEW meter
        const populatedFields = getPopulatedDefaultFields();
        const requiredFields = populatedFields.filter(
          (field) => field.required,
        );

        for (const field of requiredFields) {
          const fieldValue = meterInfo[field.name as keyof typeof meterInfo];

          // Special handling for dropdown fields (objects with value property)
          if (field.type === "dropdown") {
            if (
              !fieldValue ||
              !(fieldValue as any)?.value ||
              (fieldValue as any)?.value === ""
            ) {
              setToast({
                message: `${field.label} is required`,
                severity: "error",
              });
              return;
            }
          } else {
            // Regular validation for other field types
            if (!fieldValue || fieldValue === "") {
              setToast({
                message: `${field.label} is required`,
                severity: "error",
              });
              return;
            }
          }
        }
      }

      // Set loading state
      if (window.setIsLoading) {
        window.setIsLoading(true);
      }

      try {
        let meterIdToUse = null;

        if (meterInfo.assignExistingMeter) {
          // Use existing meter ID directly
          meterIdToUse = meterInfo.existingMeterNumber.trim();
          console.log("DEBUG: Using existing meterIdToUse:", meterIdToUse);
        } else {
          // Create NEW meter
          const requestData: CreateMeterRequestData = {
            tenantId: {
              id: "e936e050-e1d7-11ee-8baa-37d11d926d57",
              entityType: "TENANT",
            },
            name: generateUniqueName(),
            type: "METER",
            label: "NY Building",
            additionalInfo: {
              registerData: [],
              connectionStatus: meterInfo?.connectionStatus
                ? "Connected"
                : "Disconnected", // Store as string
              meterCommodityType:
                ((meterInfo?.meterType as any)?.value as string) || "",
              manufacturer:
                ((meterInfo?.manufacturer as any)?.value as string) || "",
              SerialNumber: meterInfo?.meterSerialNo || "",
              BadgeNumber: meterInfo?.meterBadgeNo || "",
              InstallationDate: meterInfo?.meterInstallDate?.toString(),
              UseAsLoadProfile: Boolean(meterInfo?.useAsLoadProfile),
              status: meterInfo?.status || "Inactive",
              siteId: meterInfo?.siteId || "",
              existingMeterNumber: meterInfo?.existingMeterNumber || "",
              lineLtCtRatio: meterInfo?.lineLtCtRatio || "",
              meterBoxDimensions: meterInfo?.meterBoxDimensions || "",
              meterLegalStatus: meterInfo?.meterLegalStatus || "",
              remarks: meterInfo?.remarks || "",
              ...dynamicTypeSpecificFields.reduce((acc, field) => {
                const value = meterInfo[field.name as keyof typeof meterInfo];

                if (value !== undefined && value !== null && value !== "") {
                  if (field.type === "dropdown" && (value as any)?.value) {
                    acc[field.name] = (value as any)?.value;
                  } else if (field.type === "date" && value) {
                    try {
                      acc[field.name as keyof typeof acc] = formatDate(
                        value.toString(),
                        "YYYY-MM-DD",
                      );
                    } catch (dateError: any) {
                      console.error(
                        `Error formatting date for field ${field.name}:`,
                        dateError,
                      );
                      acc[field.name] = null;
                    }
                  } else if (field.type === "checkbox") {
                    acc[field.name] = Boolean(value);
                  } else if (field.type === "number") {
                    acc[field.name] = Number(value) || 0;
                  } else {
                    acc[field.name] = String(value);
                  }
                }
                return acc;
              }, {}),
            },
            customer_id: meterInfo?.assignToCustomer?.value || "",
            device_profile_id: "",
            device_data: "",
            firmware_id: "",
            software_id: "",
            external_id: "",
            version: 0,
          };

          // Only add ID if it's an update (existing meter)
          if (meterInfo?.id && meterInfo?.id?.id) {
            requestData.id = {
              id: meterInfo?.id?.id,
              entityType: "DEVICE",
            };
          }

          // Validate critical fields for creation
          if (!requestData.additionalInfo.meterCommodityType) {
            throw new Error("Meter  type is missing");
          }
          if (!requestData.additionalInfo.manufacturer) {
            throw new Error("Manufacturer is missing");
          }
          if (!requestData.additionalInfo.siteId) {
            throw new Error("Site ID is missing");
          }

          const response = await fetchPOSTRequest(`device`, requestData);
          if (response?.id?.id) {
            meterIdToUse = response.id.id;
          } else {
            throw new Error("No response ID received from server");
          }
        }

        if (meterIdToUse) {
          // Determine which customer ID to use for linking
          let customerIdToLink: string | null = null;

          if (meterInfo?.assignToCustomer?.value) {
            customerIdToLink = String(meterInfo.assignToCustomer.value);
          } else if (stepUUIDs.consumer) {
            customerIdToLink = String(stepUUIDs.consumer);
          }

          // Link the meter to the customer
          let linkResponse = null;

          if (customerIdToLink) {
            try {
              linkResponse = await fetchPOSTRequest(
                `customer/${customerIdToLink}/device/${meterIdToUse}`,
                {},
                true,
              );

              // Update consumer with the new meter ID in additionalInfo
              if (linkResponse?.status === 200) {
                try {
                  // Validate all parameters before calling updateConsumerWithAssetId
                  if (!customerIdToLink || customerIdToLink === "undefined") {
                    throw new Error("Customer ID is undefined or invalid");
                  }
                  if (!meterIdToUse || meterIdToUse === "undefined") {
                    throw new Error("Meter ID is undefined or invalid");
                  }

                  await updateConsumerWithAssetId(
                    customerIdToLink,
                    "meter",
                    meterIdToUse,
                  );
                } catch (updateError: any) {
                  console.error("Error details:", {
                    customerId: customerIdToLink,
                    meterId: meterIdToUse,
                    errorMessage: updateError.message,
                  });
                  setToast({
                    message:
                      "Meter linked but failed to update consumer info. Please try again.",
                    severity: "warning",
                  });
                }
              } else {
                setToast({
                  message: `Failed to link to customer. Status: ${linkResponse?.status}`,
                  severity: "warning",
                });
              }
            } catch (linkError: any) {
              console.error(
                "❌ Error during customer/device linking:",
                linkError,
              );
              setToast({
                message: "Failed to link to customer due to API error.",
                severity: "warning",
              });
            }
          }

          // Update the step UUID
          updateStepUUID("meter", meterIdToUse);

          showStatus(
            "success",
            "Consumer Created Successfully",
            "What would you like to do next?",
            {
              primary: {
                label: "Create Another Consumer",
                onClick: () => {
                  onFinalCompletion(false); // don't close modal, just reset
                  handleStatusModalClose();
                },
              },
              secondary: {
                label: "Stay on This Page",
                onClick: () => {
                  handleStatusModalClose();
                },
              },
              tertiary: {
                label: "View Consumer Details (in 360)",
                onClick: () => {
                  handleStatusModalClose();
                  setOpenModal({ type: "", id: "", onlyMeter: false });
                  navigate(`/consumer-360?view=details&id=${customerIdToLink}`);
                },
              },
            },
          );
        }
      } catch (error: any) {
        console.error("❌ Error during meter creation:", error);
        console.error("Error details:", {
          message: error.message,
          response: error.response?.data,
          status: error.response?.status,
        });

        const errorMessage = extractErrorMessage(error);

        setToast({
          message: errorMessage,
          severity: "error",
        });

        showStatus("error", errorMessage);
      } finally {
        // Reset loading state
        if (window.setIsLoading) {
          window.setIsLoading(false);
        }
      }
    },
    [
      meterInfo,
      setToast,
      stepUUIDs,
      dynamicTypeSpecificFields,
      updateStepUUID,
      onFinalCompletion,
      updateConsumerWithAssetId,
      generateUniqueName,
      getPopulatedDefaultFields,
      handleResetMeterInfo,
    ],
  );

  const fetchMeterDetails = async (payload?: string) => {
    const searchValueToUse = payload || meterInfo.existingMeterNumber;
    if (!searchValueToUse || !searchValueToUse.trim()) {
      setToast({
        message: "Please enter a valid search value.",
        severity: "error",
      });
      return;
    }

    if (window.setIsLoading) {
      window.setIsLoading(true);
    }

    try {
      const response = await fetchGETRequest(
        `device/${searchValueToUse.trim()}`,
        {},
        true,
      );

      if (response?.data?.additionalInfo) {
        const {
          InstallationDate,
          UseAsLoadProfile,
          meterCommodityType,
          manufacturer,
          status,
          connectionStatus,
          SerialNumber,
          BadgeNumber,
          siteId,
          existingMeterNumber,
          lineLtCtRatio,
          meterBoxDimensions,
          meterLegalStatus,
          remarks,
        } = response?.data?.additionalInfo;

        setMeterInfo({
          ...meterInfo,
          manufacturer: Manufacturer_options.find(
            (item: any) => item.value === manufacturer,
          ) || { value: "", label: "" },
          meterType: meterTypeOptions.find(
            (item: any) => item.value === meterCommodityType,
          ) || { value: "", label: "" },
          meterSerialNo: SerialNumber,
          meterBadgeNo: BadgeNumber,
          meterInstallDate: isNaN(new Date(InstallationDate).getTime())
            ? null
            : new Date(InstallationDate),
          useAsLoadProfile: !!UseAsLoadProfile,
          status: status || "Inactive",
          siteId: siteId || "",
          existingMeterNumber: existingMeterNumber || searchValueToUse || "",
          lineLtCtRatio: lineLtCtRatio,
          meterBoxDimensions: meterBoxDimensions,
          meterLegalStatus: meterLegalStatus,
          remarks: remarks,
          assignToCustomer: dropdownOptions.assignToCustomer?.find(
            (item: any) => item.value === response?.data?.customerId?.id,
          ),
          id: response?.data?.id,
          connectionStatus: connectionStatus === "Connected",
        });

        showStatus("success", "Meter details fetched successfully.");
      } else {
        setToast({ message: "Meter not found", severity: "error" });
      }
    } catch (error: any) {
      console.error("Error fetching meter details:", error);
      setToast({
        message: "Failed to fetch meter details. Please check the ID.",
        severity: "error",
      });
    } finally {
      if (window.setIsLoading) {
        window.setIsLoading(false);
      }
    }
  };

  useEffect(() => {
    if (location?.state?.meterid && !meterInfo.existingMeterNumber) {
      setMeterInfo((prev) => ({
        ...prev,
        existingMeterNumber: location.state.meterid,
      }));
      fetchMeterDetails(location.state.meterid);
    }
  }, [location]);

  useEffect(() => {
    fetchCustomers();
    generateUniqueName();
  }, []);

  useEffect(() => {
    const fetchCustomFields = async () => {
      try {
        const response = await fetchGETRequest(
          `asset/${customFieldId}`,
          {},
          true,
        );

        if (response?.data?.additionalInfo) {
          const { customFields } = response?.data?.additionalInfo;
          setCreatedFields?.(customFields);
        }
      } catch (error: any) {
        console.error("Error fetching consumer:", error.message);
      }
    };
    fetchCustomFields();
  }, []);

  const fetchCustomers = useCallback(async () => {
    try {
      const customerResponse = await fetchGETRequest(
        `customers?pageSize=10&page=0`,
        {},
      );
      const transformedArray = customerResponse?.data?.map((customer: any) => ({
        label: customer.name,
        value: customer.id.id,
      }));
      setDropdownOptions((prev) => ({
        ...prev,
        assignToCustomer: transformedArray,
      }));
    } catch (error: any) {
      console.error("Error fetching customers:", error);
    }
  }, []);

  const getCustomFields = (item: any) => {
    switch (item?.type?.label) {
      case "Date":
        return (
          <DatePickerInput
            label={item?.title}
            format={"dd/MM/yyyy"}
            placeholder={"dd/mm/yyyy"}
            value={meterInfo?.[item?.title?.toUpperCase()] || null}
            onChange={(event) => {
              setMeterInfo({
                ...meterInfo,
                [item?.title?.toUpperCase()]: event,
              });
            }}
          />
        );
      case "Checkbox":
        return (
          <Stack mt={"30px"}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={meterInfo?.[item?.title?.toUpperCase()] || false}
                  onChange={(event) => {
                    setMeterInfo({
                      ...meterInfo,
                      [item?.title?.toUpperCase()]: event.target.checked,
                    });
                  }}
                  sx={{
                    color: "#1976d2",
                    "&.Mui-checked": {
                      color: "#1976d2",
                    },
                  }}
                />
              }
              label={item?.title}
            />
          </Stack>
        );
      case "Text":
        return (
          <>
            <CustomInput
              label={item?.title}
              placeholder={item?.title}
              type="text"
              value={meterInfo?.[item?.title?.toUpperCase()] || ""}
              onChange={(event) => {
                setMeterInfo({
                  ...meterInfo,
                  [item?.title?.toUpperCase()]: event.target.value,
                });
              }}
            />
          </>
        );

      case "Number":
        return (
          <>
            <CustomInput
              label={item?.title}
              placeholder={item?.title}
              type="number"
              value={meterInfo?.[item?.title?.toUpperCase()] || ""}
              onChange={(event) => {
                setMeterInfo({
                  ...meterInfo,
                  [item?.title?.toUpperCase()]: event.target.value,
                });
              }}
            />
          </>
        );

      case "Dropdown":
        return (
          <>
            <CustomOutlinedSelect
              label={item?.title}
              placeholder={item?.title}
              options={item?.options}
              value={meterInfo?.[item?.title?.toUpperCase()] || ""}
              onChange={(event) => {
                setMeterInfo({
                  ...meterInfo,
                  [item?.title?.toUpperCase()]: event,
                });
              }}
            />
          </>
        );

      default:
        return null;
    }
  };

  const handleTabChange = (_: any, newValue: string) => {
    setSelectedTab(newValue);
  };

  // Expose the save function to the global scope for the footer save button
  useEffect(() => {
    window.handleSaveMeter = (skipNext?: boolean) => handleSave(skipNext);
    return () => {
      delete window.handleSaveMeter;
    };
  }, [handleSave]);

  // Auto-fill site ID from previous step
  useEffect(() => {
    if (stepUUIDs.site && !meterInfo.siteId) {
      setMeterInfo((prev: any) => ({
        ...prev,
        siteId: stepUUIDs.site,
      }));
    }
  }, [stepUUIDs.site, meterInfo.siteId]);

  const dynamicTypeSpecificFieldsArray = useMemo(() => {
    const fields = getTypeSpecificConsumerFields("meter");
    const enrichedFields = fields.map((field) => {
      if (field.type === "select") {
        if (dropdownOptions[field.name as keyof typeof dropdownOptions]) {
          return {
            ...field,
            options:
              dropdownOptions[field.name as keyof typeof dropdownOptions],
          };
        }
      }
      return field;
    });

    return enrichedFields;
  }, [dropdownOptions]);

  return (
    <Box>
      <Card sx={{ mt: "20px" }}>
        <TabContext value={selectedTab}>
          <CardHeader
            sx={{ paddingBottom: 0 }}
            title={
              <Stack
                direction={"row"}
                justifyContent={"space-between"}
                alignItems={"center"}
              >
                <TabList
                  onChange={handleTabChange}
                  variant={"scrollable"}
                  scrollButtons={false}
                  TabIndicatorProps={{
                    style: {
                      backgroundColor: "##1280E7",
                      height: "3px",
                    },
                  }}
                >
                  {TABS.map((tab) => {
                    return (
                      <Tab
                        label={tab}
                        value={tab}
                        className="consumer_right_card_list_tab"
                      />
                    );
                  })}
                </TabList>

                <ContainedButton
                  label={"Create custom fields"}
                  sx={{
                    width: "fit-content",
                    fontWeight: 600,
                    color: "#ffffff",
                  }}
                  onClick={() => toggleCustomFieldDrawer?.("Meter", true)}
                />
              </Stack>
            }
          />
          <Divider />

          <CardContent>
            <TabPanel value={"Meters"} sx={{ padding: 0 }}>
              <Grid
                container
                spacing={2}
                alignItems="center"
                sx={{ mt: "10px", mb: "0px" }}
              >
                <Grid size={{ xs: 12, md: 4, lg: 4 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={meterInfo?.assignExistingMeter || false}
                        onChange={(e) =>
                          setMeterInfo({
                            ...meterInfo,
                            assignExistingMeter: e.target.checked,
                          })
                        }
                        sx={{
                          color: "#1976d2",
                          "&.Mui-checked": {
                            color: "#1976d2",
                          },
                        }}
                      />
                    }
                    label={"Assign Existing Meter"}
                  />
                </Grid>

                {meterInfo?.assignExistingMeter && (
                  <>
                    <Grid size={{ xs: 12, md: 8, lg: 8 }}>
                      <Stack direction="row" spacing={1} alignItems="flex-end">
                        <CustomInput
                          label={"Existing Meter ID"}
                          placeholder={"Enter Meter ID"}
                          value={meterInfo.existingMeterNumber}
                          onChange={(e) =>
                            setMeterInfo((prev) => ({
                              ...prev,
                              existingMeterNumber: e.target.value,
                            }))
                          }
                        />
                        <ContainedButton
                          label="Search"
                          onClick={() => fetchMeterDetails()}
                          sx={{ height: "40px", mb: "4px" }}
                        />
                      </Stack>
                    </Grid>

                    <Grid size={{ xs: 12 }}>
                      <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                        Meter Details (from Search)
                      </Typography>
                    </Grid>

                    <Grid size={{ xs: 12, md: 4, lg: 4 }} mt={"10px"}>
                      <CustomOutlinedSelect
                        label={"Meter Type"}
                        placeholder={"Select Meter Type"}
                        options={dropdownOptions.meterType}
                        value={meterInfo?.meterType}
                        onChange={handleMeterTypeChange}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, md: 4, lg: 4 }} mt={"10px"}>
                      <CustomOutlinedSelect
                        label={"Manufacturer"}
                        placeholder={"Select Manufacturer"}
                        options={Manufacturer_options}
                        value={meterInfo?.manufacturer}
                        onChange={(e: any) =>
                          setMeterInfo({ ...meterInfo, manufacturer: e })
                        }
                      />
                    </Grid>
                    <Grid size={{ xs: 12, md: 4, lg: 4 }} mt={"10px"}>
                      <CustomInput
                        label={"Site ID"}
                        placeholder={"Site ID"}
                        value={meterInfo.siteId}
                        onChange={(e) =>
                          setMeterInfo({ ...meterInfo, siteId: e.target.value })
                        }
                      />
                    </Grid>
                    <Grid size={{ xs: 12, md: 4, lg: 4 }} mt={"10px"}>
                      <CustomInput
                        label={"Serial Number"}
                        placeholder={"Enter Serial Number"}
                        value={meterInfo.meterSerialNo}
                        onChange={(e) =>
                          setMeterInfo({
                            ...meterInfo,
                            meterSerialNo: e.target.value,
                          })
                        }
                      />
                    </Grid>
                    <Grid size={{ xs: 12, md: 4, lg: 4 }} mt={"10px"}>
                      <CustomInput
                        label={"Badge Number"}
                        placeholder={"Enter Badge Number"}
                        value={meterInfo.meterBadgeNo}
                        onChange={(e) =>
                          setMeterInfo({
                            ...meterInfo,
                            meterBadgeNo: e.target.value,
                          })
                        }
                      />
                    </Grid>
                    <Grid size={{ xs: 12, md: 4, lg: 4 }} mt={"10px"}>
                      <DatePickerInput
                        label={"Install Date"}
                        placeholder={"dd/mm/yyyy"}
                        value={meterInfo.meterInstallDate}
                        onChange={(e) =>
                          setMeterInfo({ ...meterInfo, meterInstallDate: e })
                        }
                      />
                    </Grid>
                  </>
                )}
              </Grid>

              {!meterInfo?.assignExistingMeter && (
                <>
                  <Grid container spacing={2} sx={{ mt: "10px" }}>
                    <Grid size={{ xs: 12, md: 4, lg: 4 }} mt={"10px"}>
                      <Stack mt={"30px"}>
                        <CustomSwitch
                          label={
                            meterInfo?.connectionStatus
                              ? "Connected"
                              : "Disconnected"
                          }
                          value={meterInfo?.connectionStatus}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                            setMeterInfo({
                              ...meterInfo,
                              connectionStatus: e.target.checked,
                            })
                          }
                        />
                      </Stack>
                    </Grid>

                    <Grid size={{ xs: 12, md: 4, lg: 4 }} mt={"10px"}>
                      <Stack mt={"30px"}>
                        <CustomSwitch
                          label={"Use As Load Profile"}
                          value={meterInfo?.useAsLoadProfile}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                            setMeterInfo({
                              ...meterInfo,
                              useAsLoadProfile: e.target.checked,
                            })
                          }
                        />
                      </Stack>
                    </Grid>
                  </Grid>

                  <Grid container spacing={2}>
                    {dynamicTypeSpecificFieldsArray
                      .filter(
                        (field) =>
                          field.name !== "connectionStatus" &&
                          field.name !== "useAsLoadProfile",
                      )
                      .map((field) => (
                        <Grid
                          key={field.name}
                          size={
                            field.fullWidth
                              ? { xs: 12 }
                              : { xs: 12, md: 4, lg: 4 }
                          }
                          mt={"10px"}
                        >
                          {renderTypeSpecificField(
                            field,
                            meterInfo,
                            setMeterInfo,
                          )}
                        </Grid>
                      ))}
                  </Grid>

                  {createdFields?.["Meter"] && (
                    <>
                      <Stack sx={{ paddingTop: "30px", paddingBottom: "20px" }}>
                        <Divider sx={{ borderWidth: "1.5px" }} />
                      </Stack>

                      <Grid container spacing={2}>
                        {createdFields?.["Meter"]?.map((item: any) => {
                          return (
                            <Grid size={{ xs: 12, md: 4, lg: 4 }} mt={"10px"}>
                              {getCustomFields(item)}
                            </Grid>
                          );
                        })}
                      </Grid>
                    </>
                  )}
                </>
              )}
            </TabPanel>

            <TabPanel value={"Read Cluster"} sx={{ padding: 0 }}>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, md: 4, lg: 4 }}>
                  <DatePickerInput
                    label={"Start Date"}
                    placeholder={"dd/mm/yyyy hh:mm"}
                    value={readClusterDetail?.startDate || ""}
                    onChange={(event) => {
                      setReadClusterDetail({
                        ...readClusterDetail,
                        startDate: event || new Date(),
                      });
                    }}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 4, lg: 4 }}>
                  <DatePickerInput
                    label={"End Date"}
                    value={readClusterDetail?.endDate || ""}
                    placeholder={"dd/mm/yyyy hh:mm"}
                    onChange={(event) => {
                      setReadClusterDetail({
                        ...readClusterDetail,
                        endDate: event || new Date(),
                      });
                    }}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 4, lg: 4 }}>
                  <CustomOutlinedSelect
                    label={"Register Type Code"}
                    options={dropdownOptions.meterType}
                    placeholder={"Enter or Select a Register Type"}
                    value={readClusterDetail.registerTypeCode}
                    onChange={(event) => {
                      setReadClusterDetail({
                        ...readClusterDetail,
                        registerTypeCode: {
                          value: (event?.value as string) || "",
                          label: (event?.label as string) || "",
                        },
                      });
                    }}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 12, lg: 12 }} mt={"10px"}>
                  <ContainedButton
                    label={"Search"}
                    sx={{
                      width: "fit-content",
                      fontWeight: 600,
                      color: "#ffffff",
                    }}
                    onClick={() =>
                      navigate("/read-cluster", {
                        state: {
                          ...readClusterDetail,
                          searchValue: meterInfo.existingMeterNumber,
                        },
                      })
                    }
                    disabled={
                      !meterInfo.existingMeterNumber ||
                      !readClusterDetail?.startDate ||
                      !readClusterDetail?.endDate ||
                      !readClusterDetail?.registerTypeCode
                    }
                  />
                </Grid>
              </Grid>
            </TabPanel>
          </CardContent>
        </TabContext>
      </Card>

      {/* Save Button for Meter */}
      {/* <Box sx={{ mt: 2, display: "flex", justifyContent: "flex-end" }}>
        <ContainedButton
          label={"Save Meter"}
          
          sx={{
            width: "fit-content",
            fontWeight: 600,
            color: "#ffffff",
          }}
          onClick={handleSave}
        />
      </Box> */}
    </Box>
  );
};
